<template>
  <v-app>
    <mainMenu></mainMenu>
    <v-container fluid>
      <!-- Propose and confirm: requests other shops made on this user's
           bookings overnight, and ones they made themselves. -->
      <proposal-panel @changed="afterProposal" />

      <upcountry-booking-dialog v-if="upDialog" v-model="upDialog" :fields="upFields" :province="upProvince"
        @saved="onUpSaved" />

      <!-- นัดหมายรอ SO: a group supervisor's worklist. Some groups' PCs book
           without an SO while head office creates it in SAP, and the head fills
           the number in -- on bookings they do not own, which is why this is not
           the ordinary list below (that one shows a shop its own bookings only).
           Rows are the group's live นัดหมาย that X.canAttachSo lets this login
           attach to; the button opens the same ใส่ SO dialog as the list. -->
      <v-card v-if="isSupervisor" class="mb-4">
        <v-toolbar color="indigo" dark dense flat>
          <v-toolbar-title>นัดหมายรอ SO</v-toolbar-title>
          <v-spacer />
          <span class="caption">กลุ่ม {{ myProfile.bookingGroup }} · {{ awaitingSoRows.length }} รายการ</span>
        </v-toolbar>
        <v-card-text v-if="!awaitingSoRows.length" class="body-2">
          ไม่มีนัดหมายในกลุ่มที่รอเลขที่ SO
        </v-card-text>
        <v-simple-table v-else dense>
          <thead>
            <tr>
              <th>วันส่ง</th>
              <th>ลูกค้า</th>
              <th>เบอร์โทร</th>
              <th>ผู้จอง</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in awaitingSoRows" :key="b.id">
              <td class="text-no-wrap">{{ b.date }} · {{ labelTh[b.slot] }}</td>
              <td>
                {{ b.custName || b.name }}
                <div v-if="b.sourceRef" class="caption grey--text">เลขที่คำสั่งซื้อ {{ b.sourceRef }}</div>
              </td>
              <td class="text-no-wrap">{{ b.telephone }}</td>
              <td>{{ $personName(b.user) }}</td>
              <td class="text-right">
                <v-btn small outlined color="indigo" :loading="busyId === b.id"
                       @click="openAttachSo(b)">ใส่ SO</v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>

      <!-- ออเดอร์รถใหญ่ต่างจังหวัด: this login's own live up-country orders
           (U.myOrders). The day list below cannot show them -- a pooled order
           has no date -- so without this card an order vanished from its
           owner's view the moment it was saved. Staff see their own here too;
           the whole pool is the รถใหญ่ต่างจังหวัด page's. Hidden when empty. -->
      <!-- Collapsed by default: while delivery books large trucks for every shop,
           a delivery login's list runs long, and it sits above the booking form. -->
      <v-card v-if="myUpOrders.length" class="mb-4">
        <v-toolbar color="deep-orange" dark dense flat class="section-toggle"
                   @click="upOrdersOpen = !upOrdersOpen">
          <v-toolbar-title>ออเดอร์รถใหญ่ต่างจังหวัด</v-toolbar-title>
          <v-spacer />
          <span class="caption mr-2">{{ myUpOrders.length }} รายการ</span>
          <v-icon>{{ upOrdersOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-toolbar>
        <v-expand-transition>
        <v-simple-table v-show="upOrdersOpen" dense>
          <thead>
            <tr>
              <th>เลขที่ SO / RT</th>
              <th>ลูกค้า</th>
              <th>จังหวัด</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in myUpOrders" :key="b.id">
              <td class="text-no-wrap">{{ upRef(b) }}</td>
              <td>
                {{ b.custName || b.name }}
                <div class="caption grey--text">{{ b.telephone }}</div>
              </td>
              <td class="text-no-wrap">
                {{ b.upProvince }}
                <div v-if="b.upRegNum" class="caption grey--text">รถ {{ b.upRegNum }}</div>
              </td>
              <td>
                <upcountry-order-actions :order="b" />
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        </v-expand-transition>
      </v-card>

      <v-card class="mb-4">
        <v-toolbar color="orange accent-3" dark dense flat>
          <v-toolbar-title>จองวันส่งสินค้า</v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <!-- What became of a booking handed over from another system (?appt=1).
               It stays until the next booking starts, so cancelling the return
               does not lose the way back. -->
          <v-alert v-if="apptResult" dense text class="mb-4"
                   :type="apptResult.rejected ? 'warning' : 'info'">
            <template v-if="apptResult.rejected">
              ส่งวันส่งกลับไปที่ {{ apptResult.rejected }} ไม่ได้ — ปลายทางนี้ไม่ได้อยู่ในรายการที่อนุญาต
              โปรดบันทึกวันส่งในระบบขายออนไลน์เอง และแจ้ง IT ให้เพิ่มโดเมนนี้
            </template>
            <template v-else>
              <div>
                <template v-if="apptResult.posted">ส่งวันส่งกลับไปที่ {{ apptResult.host }} แล้ว</template>
                <template v-else-if="apptResult.seconds > 0">
                  กำลังกลับไปที่ {{ apptResult.host }} ใน {{ apptResult.seconds }} วินาที
                </template>
                <template v-else>ส่งวันส่งกลับไปที่ {{ apptResult.host }}</template>
              </div>
              <div class="mt-2">
                <v-btn small color="orange accent-3" dark class="mr-2" @click="goBackToSource">
                  {{ apptResult.posted ? 'เปิดหน้าคำสั่งซื้อ' : 'กลับเลย' }}
                </v-btn>
                <v-btn v-if="apptResult.posted && apptResult.canFocus" small text class="mr-2" @click="focusSource">
                  สลับไปที่แท็บนั้น
                </v-btn>
                <v-btn v-if="apptResult.seconds > 0" small text @click="stopApptCountdown">ยกเลิก</v-btn>
              </div>
            </template>
          </v-alert>

          <!-- ขั้นที่ 1 — ระบุงาน -->
          <div class="subtitle-1 font-weight-bold mb-2">ขั้นที่ 1 — ระบุงาน</div>

          <v-btn-toggle v-model="mode" mandatory color="orange accent-3" class="mb-4">
            <v-btn value="so">มีเลขที่ SO</v-btn>
            <v-btn value="appointment">นัดหมาย (ยังไม่มี SO)</v-btn>
            <v-btn value="rt">รับคืน (มีเลขที่ RT)</v-btn>
          </v-btn-toggle>

          <v-row v-if="mode === 'rt'" dense>
            <v-col cols="12" sm="6" md="4">
              <v-text-field v-model="rtNumber" label="เลขที่ RT" color="orange accent-3" clearable
                            :disabled="!!soData" @keydown.enter="lookupRt" />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-btn v-if="!soData" color="orange accent-3" dark :loading="loadingSo" @click="lookupRt">ค้นหา</v-btn>
              <v-btn v-else text @click="resetJob">เปลี่ยนเลขที่ RT</v-btn>
            </v-col>
          </v-row>

          <v-row v-if="mode === 'so'" dense>
            <v-col cols="12" sm="6" md="4">
              <v-text-field v-model="soNumber" label="เลขที่ SO" color="orange accent-3" clearable
                            :disabled="!!soData" @keydown.enter="lookupSo" />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-btn v-if="!soData" color="orange accent-3" dark :loading="loadingSo" @click="lookupSo">
                ค้นหา
              </v-btn>
              <v-btn v-else text @click="resetJob">เปลี่ยนเลขที่ SO</v-btn>
            </v-col>
          </v-row>

          <v-row v-if="fromSap && soData" dense>
            <v-col cols="12" md="6">
              <v-text-field :value="soData.custName" label="ชื่อลูกค้า" readonly />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field :value="soData.custTel" label="เบอร์โทร" readonly />
            </v-col>
            <v-col cols="12">
              <v-textarea :value="soData.custAddress" label="ที่อยู่" rows="2" readonly />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.geo" label="พิกัด (ละติจูด, ลองจิจูด)" :rules="[rules.geo]"
                            clearable color="orange accent-3" placeholder="13.7563, 100.5018"
                            hint="เปิดแผนที่ คลิกขวาตำแหน่งที่ต้องการ แล้วคลิกที่พิกัดเพื่อคัดลอก จากนั้นวางที่ช่องนี้"
                            persistent-hint hide-details="auto">
                <template v-slot:append-outer>
                  <v-btn small color="orange accent-3" dark @click="openMap">
                    <v-icon left small>mdi-map-marker</v-icon>เปิดแผนที่
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.landmark" label="จุดสังเกต" :counter="landmarkMax" :maxlength="landmarkMax" color="orange accent-3" clearable
                            hint="เช่น บ้านหลังสีฟ้า ตรงข้ามร้านสะดวกซื้อ ปากซอยมีร้านกาแฟ"
                            persistent-hint hide-details="auto" />
            </v-col>
          </v-row>
          <v-alert v-if="(mode === 'so' || mode === 'rt') && soData && rtData && rtData.fields" dense outlined type="info" class="mb-4">
            <strong>{{ mode === 'rt' ? 'รับคืนสินค้า' : 'เปลี่ยนสินค้า — มีรับคืน' }} RT {{ rtData.fields.rtNo }}</strong>
            <div v-for="(p, i) in rtLines" :key="i" class="body-2">{{ p.name }} × {{ p.qty }}</div>
            <div v-if="rtData.rt && rtData.rt.soNo" class="caption">ใบขายเดิม SO {{ rtData.rt.soNo }}</div>
            <div v-if="rtData.note" class="caption">{{ rtData.note }}</div>
          </v-alert>
          <v-alert v-else-if="(mode === 'so' || mode === 'rt') && soData && rtData && rtData.note" dense text type="warning" class="mb-4">
            {{ rtData.note }}
          </v-alert>

          <!-- The :rules only bite because submitBooking calls jobForm.validate(). -->
          <v-alert v-if="mode === 'appointment' && apptHandover" dense text type="info" class="mb-4">
            ข้อมูลลูกค้ามาจากระบบขายออนไลน์ — โปรดตรวจสอบ แล้วเลือก ตำบล/อำเภอ และวัน/ช่วงเวลาที่จะส่ง
          </v-alert>

          <v-form v-if="mode === 'appointment'" ref="jobForm">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.name" label="ชื่อลูกค้า" :rules="[rules.required]" clearable
                              color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.telephone" label="เบอร์โทร" :rules="[rules.required, rules.tel]" clearable
                              hint="เบอร์ต่างประเทศ: + รหัสประเทศ เช่น +856 20 5555 1234"
                              color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="12">
                <!-- One row that grows: ตำบล/อำเภอ/จังหวัด and รหัสไปรษณีย์ have their own
                     fields, so this holds only the house number and street and rarely
                     needs a second line. -->
                <v-textarea v-model="form.address" label="ที่อยู่" rows="1" auto-grow
                            :rules="[rules.required]" clearable
                            color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.zipcode" label="รหัสไปรษณีย์" :rules="[rules.required, rules.zipCode]" clearable
                              maxlength="5" color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="form.location" :items="subDistrictOptions" label="ตำบล/อำเภอ/จังหวัด"
                          :rules="[rules.required]"
                          :loading="loadingZones" :disabled="!subDistrictOptions.length" clearable
                          color="orange accent-3" :hint="locationHint" persistent-hint hide-details="auto" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.geo" label="พิกัด (ละติจูด, ลองจิจูด)" :rules="[rules.geo]"
                              clearable color="orange accent-3" placeholder="13.7563, 100.5018"
                              hint="เปิดแผนที่ คลิกขวาตำแหน่งที่ต้องการ แล้วคลิกที่พิกัดเพื่อคัดลอก จากนั้นวางที่ช่องนี้"
                              persistent-hint hide-details="auto">
                  <template v-slot:append-outer>
                    <v-btn small color="orange accent-3" dark @click="openMap">
                      <v-icon left small>mdi-map-marker</v-icon>เปิดแผนที่
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.landmark" label="จุดสังเกต" :counter="landmarkMax" :maxlength="landmarkMax" color="orange accent-3" clearable
                              hint="เช่น บ้านหลังสีฟ้า ตรงข้ามร้านสะดวกซื้อ ปากซอยมีร้านกาแฟ"
                              persistent-hint hide-details="auto" />
              </v-col>
            </v-row>
          </v-form>

          <!-- Shared by both entry points, because these three describe the WORK
               rather than the customer and a booking has work however it was
               started. The SO path needs them most: lookupSo already asks SAP for
               the product lines and fills จำนวนชิ้น from them, but until now the
               whole block sat inside the นัดหมาย form, so staff booking against an
               SO could neither see the number nor correct it. None of the three
               carries a :rules, so living outside jobForm costs no validation --
               เวลาติดตั้ง is range-checked in submitBooking instead, which runs for
               both modes. -->
          <v-row v-if="mode === 'appointment' || soData" dense>
            <!-- What SAP actually said, line by line. The total below it is the
                 number the planner uses, and staff can only judge whether it is
                 right if they can see what it was added up from -- two lines for
                 three kiosks is indistinguishable from three lines for three
                 unless the quantities are on screen. Read-only on purpose: the
                 editable total is จำนวนชิ้น, and a second place to change a
                 quantity would be a second source of truth against SAP. -->
            <v-col v-if="soProducts.length" cols="12">
              <div class="caption grey--text">สินค้าตาม SO (จาก SAP)</div>
              <div v-for="(p, i) in soProducts" :key="i" class="d-flex body-2">
                <span class="flex-grow-1 mr-3">{{ productLabel(p) }}</span>
                <span class="text-no-wrap">{{ productQty(p) || 1 }} ชิ้น</span>
              </div>
              <v-divider class="my-1" />
              <div class="caption grey--text d-flex">
                <span class="flex-grow-1">รวม</span>
                <span>{{ productTotal(soProducts) }} ชิ้น</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <!-- rows="1", like ที่อยู่ above and like the edit dialog. At rows="2" the
                   textarea slot is 56px tall against the 32px of the fields beside it,
                   so the underlines sat 24px apart and the hints floated halfway up the
                   box. auto-grow still gives a second line the moment one is typed. -->
              <v-textarea v-model="form.productList" label="รายการสินค้า" rows="1" auto-grow clearable
                          color="orange accent-3" hide-details="auto" />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="form.itemCount" label="จำนวนชิ้น" type="number" min="1"
                            color="orange accent-3" clearable :hint="itemCountHint"
                            persistent-hint hide-details="auto" />
            </v-col>
            <v-col cols="12" md="3">
              <!-- clearable IS the reset: an empty field means "use the calculated
                   value", which is what the hint says and what minutesForBooking does
                   with a blank. A separate คืนค่า button would say the same thing twice. -->
              <v-text-field v-model="form.installMinutes" label="เวลาติดตั้ง (นาที)" type="number"
                            :min="minInstallMinutes" :max="maxInstallMinutes" step="15"
                            color="orange accent-3" clearable :hint="installMinutesHint"
                            persistent-hint hide-details="auto" />
            </v-col>
          </v-row>

          <v-divider class="my-4" />
          <div class="subtitle-1 font-weight-bold mb-2">ขั้นที่ 2 — เลือกวันและช่วงเวลา</div>
          <v-alert dense text type="info" class="mb-3">
            จองได้ตั้งแต่วันที่ {{ earliest }} ถึงวันที่ {{ latest }}
            (หลังวันสั่งซื้อ {{ configLeadDays }} วันทำการ ไม่นับวันอาทิตย์ และเปิดจองล่วงหน้าไม่เกิน {{ configHorizonDays }} วัน)
            และไม่มีการจัดส่งวันอาทิตย์
          </v-alert>

          <!-- The seat counts on every chip below stop describing this booking
               the moment it is out of area, and a grid that silently changed
               meaning is worse than one that says so. Shown here rather than
               beside the ตำบล field because this is where the change is
               visible: full days are suddenly pickable and the x/y counts have
               gone. -->
          <!-- Two sentences, because the two audiences have different powers.
               A shop can only take a route that exists; staff can open one, and
               telling them to ask staff would be absurd. The old wording said
               the booking "will not count toward the day's jobs", which read as
               free -- while the calendar behind it visibly showed the day drop
               from 20 to 16 because the route had reserved a truck. -->
          <v-alert v-if="bookingOutOfArea" dense text type="warning" class="mb-3">
            การจองนี้อยู่นอกพื้นที่จัดส่งปกติ
            <span v-if="bookingAreaProvince">({{ bookingAreaProvince }})</span>
            <template v-if="isStaff">
              เลือกวันที่มีเส้นทางไปพื้นที่นี้อยู่แล้ว หรือเลือกวันอื่นเพื่อเปิดเส้นทางใหม่
              ซึ่งจะจองรถของวันนั้นไว้ 1 คัน
            </template>
            <template v-else>
              เลือกได้เฉพาะวันที่มีเส้นทางไปพื้นที่นี้อยู่แล้ว
              หรือติดต่อเจ้าหน้าที่เพื่อขอเปิดเส้นทางในวันอื่น
            </template>
            <div v-if="noBookableDayNote" class="mt-1 font-weight-medium">{{ noBookableDayNote }}</div>
          </v-alert>

          <div class="d-flex align-center mb-2">
            <v-btn icon @click="shiftMonth(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
            <span class="mx-3 subtitle-1">{{ monthLabel }}</span>
            <v-btn icon @click="shiftMonth(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
          </div>

          <v-row v-if="days.length" dense>
            <v-col v-for="d in days" :key="d.date" cols="12" sm="6" md="4" lg="3">
              <v-card outlined :class="{ 'grey lighten-3': d.disabled }">
                <v-card-text class="pb-1">
                  <div class="d-flex justify-space-between align-center">
                    <strong>{{ d.label }}</strong>
                    <div class="d-flex align-center">
                      <v-chip v-if="d.over" x-small color="error" dark>เกินจำนวน {{ d.counts.total }}/{{ d.caps.total }}</v-chip>
                      <span v-else-if="!d.disabled" class="caption grey--text">{{ d.counts.total }}/{{ d.caps.total }}</span>
                      <!-- ปิดรับจอง. Staff only: the rule it drives refuses shops, so
                           a shop holding the switch would be closing the door on
                           itself. .stop so the click never reaches a slot chip. -->
                      <v-btn v-if="isStaff && !d.disabled" icon x-small class="ml-1"
                             :color="d.stopped ? 'red' : 'grey'"
                             :title="d.stopped ? 'เปิดรับจองวันนี้' : 'ปิดรับจองวันนี้'"
                             @click.stop="openStopDialog(d)">
                        <v-icon small>{{ d.stopped ? 'mdi-lock' : 'mdi-lock-open-variant-outline' }}</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <div v-if="d.sundayOpen" class="mt-1">
                    <v-chip x-small color="teal" dark>วันอาทิตย์ เปิดรับพิเศษ</v-chip>
                    <span v-if="d.sundayNote" class="caption teal--text ml-1">{{ d.sundayNote }}</span>
                  </div>
                  <div v-if="d.stopped" class="mt-1">
                    <v-chip x-small color="red" dark>ปิดรับจอง</v-chip>
                    <span v-if="d.stopNote" class="caption red--text ml-1">{{ d.stopNote }}</span>
                  </div>

                  <!-- The whole point of the grouping work, and the reason it is
                       a province name and not a bare count: someone booking a
                       customer in ราชบุรี can see that the 15th already has two,
                       pick that day while the customer is still on the phone,
                       and never need to move anyone afterwards. A count alone
                       said a trip existed but not where to.

                       Its own line rather than beside the date: province names
                       are long, and squeezed into that row they pushed the
                       seat count off the card. -->
                  <div v-if="d.outOfAreaChips.length" class="mt-1">
                    <v-chip v-for="c in d.outOfAreaChips" :key="c.key" x-small class="mr-1 mb-1"
                            :dark="c.state !== 'carried'" :outlined="c.state === 'carried'"
                            :title="c.title || ''"
                            :color="c.state === 'open' || c.state === 'carried' ? 'deep-purple' : 'grey'">{{ c.text }}</v-chip>
                  </div>
                  <div v-if="d.capNote" class="caption error--text">{{ d.capNote }}</div>
                </v-card-text>
                <v-card-actions class="pt-0 flex-wrap">
                  <!-- Staff open a Sunday when the week is full. Closing it again
                       is the lock above, which on a Sunday asks for a reason. -->
                  <v-btn v-if="isStaff && d.sunday && d.disabled" small outlined color="teal" class="ma-1"
                         @click.stop="openSundayDialog(d)">
                    <v-icon small left>mdi-calendar-plus</v-icon>เปิดรับวันอาทิตย์
                  </v-btn>
                  <v-chip v-for="s in slots" :key="s" small class="ma-1"
                          :color="chipColor(d, s)" :dark="chipDark(d, s)"
                          :disabled="!canPick(d, s)"
                          :outlined="!(selected.date === d.date && selected.slot === s)"
                          @click="canPick(d, s) && pick(d.date, s)">
                    {{ labelTh[s] }} {{ chipText(d, s) }}
                  </v-chip>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
          <!-- Paging past the window is allowed, so a month with nothing in it
               has to say so rather than render as blank space. -->
          <div v-else class="grey--text text-center py-6">ไม่มีวันที่จองได้ในเดือนนี้</div>

          <v-divider class="my-4" />
          <div class="subtitle-1 font-weight-bold mb-2">ขั้นที่ 3 — ยืนยัน</div>
          <v-btn color="orange accent-3" dark large :disabled="!canSubmit" :loading="submitting"
                 @click="submitBooking">
            ยืนยันการจอง
            <span v-if="selected.date" class="ml-2">{{ selected.date }} · {{ labelTh[selected.slot] }}</span>
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- ค้นหา / จัดการการจอง. Visible to everyone, not just staff: a shop
           login needs to answer its own customers too. What differs is reach --
           staff see every booking, a shop sees only the ones its own login
           created, which bookingService applies to both the list and the
           search rather than leaving it to the template. -->
      <v-card ref="bookingList" class="mb-4">
        <v-toolbar color="orange darken-2" dark dense flat>
          <v-toolbar-title>ค้นหา / จัดการการจอง</v-toolbar-title>
          <v-spacer />
          <span class="caption">{{ canSeeAll ? 'เห็นการจองทั้งหมด' : 'เห็นเฉพาะการจองของคุณ' }}</span>
        </v-toolbar>
        <v-card-text>
          <v-row dense align="center">
            <v-col cols="12" sm="4" md="3">
              <v-text-field v-model="search.tel" label="ค้นหาด้วยเบอร์โทร" clearable
                            color="orange accent-3" hide-details="auto"
                            placeholder="081-234-5678" @keydown.enter="runSearch" />
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <v-text-field v-model="search.soNo" label="หรือเลขที่ SO" clearable
                            color="orange accent-3" hide-details="auto" @keydown.enter="runSearch" />
            </v-col>
            <v-col cols="12" sm="4" md="6">
              <v-btn color="orange accent-3" dark :loading="searching" @click="runSearch">
                <v-icon left>mdi-magnify</v-icon>ค้นหา
              </v-btn>
              <v-btn text @click="clearSearch">ล้างตัวกรอง</v-btn>
            </v-col>
          </v-row>

          <!-- Which day the list starts on, and how wide it runs. Separate from
               the search boxes above because they ask a different question:
               those find one customer's booking, these show a day's work. The
               two are orthogonal -- the date anchors the list, the toggle sets
               its width -- so neither can describe something the other is not
               showing. -->
          <v-row dense align="center" class="mt-1">
            <v-col cols="12" sm="4" md="3">
              <v-text-field v-model="search.date" label="ดูการจองตั้งแต่วันที่" type="date"
                            color="orange accent-3" hide-details="auto" />
            </v-col>
            <v-col cols="12" sm="8" md="9">
              <v-btn-toggle v-model="rangeMode" mandatory dense color="orange accent-3">
                <v-btn small value="day">รายวัน</v-btn>
                <v-btn small value="ahead">{{ aheadDays }} วันข้างหน้า</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>

          <div class="caption grey--text mt-2">
            {{ listCaption }}
            <!-- Said on the day, not only per row, so delivery can tell whether a
                 day is ready to plan without scanning the column below. -->
            <span v-if="!listLoading && missingPinCount" class="orange--text text--darken-3">
              · ไม่มีพิกัด {{ missingPinCount }}
            </span>
          </div>

          <v-data-table :headers="bookingHeaders" :items="rows" :expanded.sync="expanded"
                        item-key="id" show-expand dense
                        :loading="listLoading" loading-text="กำลังโหลด..."
                        :no-data-text="emptyText">
            <template v-slot:item.date="{ item }">
              {{ item.date }}
              <!-- A booking that took no seat on this day, shown where someone
                   reading the day's list would otherwise count it as one. Names
                   the route's state rather than the province alone, so a row in
                   the day list says the same thing the day card above it says. -->
              <v-chip v-if="item.outOfArea" x-small dark class="ml-1" :color="tripChip(item).color">
                {{ tripChip(item).text }}
              </v-chip>
            </template>
            <template v-slot:item.slot="{ item }">{{ labelTh[item.slot] }}</template>
            <!-- พิกัด. The pin is the one thing a driver's นำทาง goes to directly,
                 so a booking without one is marked where delivery will see it
                 before planning -- the same icon carBooking's slot dialog uses.
                 Clicking opens แก้ไข, which already holds the field. A cancelled
                 booking needs no pin and shows nothing. -->
            <template v-slot:item.geo="{ item }">
              <v-tooltip v-if="isLive(item)" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on"
                         :disabled="!canManage(item)" @click="openEdit(item)">
                    <v-icon :color="hasPin(item) ? 'green darken-1' : 'orange darken-3'">
                      {{ hasPin(item) ? 'mdi-map-marker-check' : 'mdi-map-marker-off-outline' }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ hasPin(item) ? 'มีพิกัดแล้ว — คลิกเพื่อแก้ไข' : 'ยังไม่มีพิกัด — คลิกเพื่อเพิ่ม' }}</span>
              </v-tooltip>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="isLive(item) ? 'green' : 'grey'" dark>
                {{ isLive(item) ? 'ใช้งาน' : 'ยกเลิกแล้ว' }}
              </v-chip>
            </template>
            <template v-slot:item.soNo="{ item }">
              {{ RJ.refText(item) }}
              <!-- Shares a stop, and so a seat, with another booking of the same
                   customer. Who with is in the expanded row. -->
              <v-chip v-if="item.visitId && isLive(item)" x-small dark color="teal darken-1" class="ml-1">
                จุดส่งเดียวกัน
              </v-chip>
            </template>
            <template v-slot:item.action="{ item }">
              <upcountry-order-actions v-if="U.isUpcountry(item)" :order="item"
                                       @changed="refreshRows" />
              <template v-else>
              <v-chip v-if="hasPending(item)" small color="amber darken-2" dark class="mr-1"
                      @click="scrollToPanel">ตัดสินคำขอก่อน</v-chip>
              <v-btn v-else small color="orange accent-3" dark class="mr-1"
                     :disabled="!canManage(item)" :loading="busyId === item.id"
                     @click="openMove(item)">ย้ายวัน</v-btn>
              <v-btn small outlined color="orange darken-2" class="mr-1"
                     :disabled="!canManage(item)" :loading="busyId === item.id"
                     @click="openEdit(item)">แก้ไข</v-btn>
              <!-- Only on a นัดหมาย. A booking already carrying an SO has nothing
                   to fill in, and attachSo refuses to overwrite one; a return-only
                   booking (RT, no SO) is not waiting for one either. -->
              <v-btn v-if="RJ.isAppointment(item)" small outlined color="indigo" class="mr-1"
                     :disabled="!canAttach(item)" :loading="busyId === item.id"
                     @click="openAttachSo(item)">ใส่ SO</v-btn>
              <v-btn v-if="!hasPending(item)" small color="error" text
                     :disabled="!canManage(item)" :loading="busyId === item.id"
                     @click="openCancel(item)">ยกเลิก</v-btn>
              </template>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="py-2">
                <div class="caption">
                  <strong>ผู้จอง:</strong> {{ $personName(item.user) || '-' }} ·
                  {{ (item.createdAt || '').slice(0, 16).replace('T', ' ') }}
                </div>
                <div v-if="item.sourceRef" class="caption">
                  <strong>เลขที่คำสั่งซื้อ (ขายออนไลน์):</strong> {{ item.sourceRef }}
                </div>
                <template v-if="item.rtNo">
                  <div class="caption"><strong>เลขที่ RT (รับคืน):</strong> {{ item.rtNo }}</div>
                  <div v-for="(p, i) in RJ.returnLines(item.prodListRt)" :key="'rt' + i" class="caption">
                    • {{ p.name }} × {{ p.qty }}
                  </div>
                </template>
                <div class="caption"><strong>ที่อยู่:</strong> {{ item.address || '-' }}</div>
                <div v-if="item.landmark" class="caption"><strong>จุดสังเกต:</strong> {{ item.landmark }}</div>
                <div v-if="item.geo" class="caption"><strong>พิกัด:</strong> {{ item.geo }}</div>

                <!-- What this stop will actually cost the day. The list is the SAP
                     rows when the booking has them and the free text when it does
                     not, and both numbers say where they came from: a count nobody
                     confirmed and a duration nobody set are precisely what someone
                     opening this row is checking for. -->
                <div class="caption mt-1"><strong>รายการสินค้า:</strong></div>
                <template v-if="bookingProducts(item).length">
                  <div v-for="(p, i) in bookingProducts(item)" :key="'p' + i" class="caption">
                    • {{ productLabel(p) }} — {{ productQty(p) || 1 }} ชิ้น
                  </div>
                </template>
                <template v-else-if="productLines(item).length">
                  <div v-for="(line, i) in productLines(item)" :key="'l' + i" class="caption">
                    • {{ line }}
                  </div>
                </template>
                <div v-else class="caption grey--text">ไม่ได้ระบุ</div>
                <div class="caption">
                  <strong>จำนวน:</strong> {{ bookingItems(item).items }} ชิ้น{{ itemSourceNote(item) }}
                  ·
                  <strong>เวลาติดตั้ง:</strong> {{ bookingInstall(item).minutes }} นาที{{ installSourceNote(item) }}
                </div>

                <!-- One destination, several bookings. Linked: who with, and the
                     undo. Not linked but the same phone, ตำบล, date and slot is
                     on the list: the offer, since the day gives back a seat. -->
                <div v-if="item.visitId && isLive(item)" class="caption mt-1">
                  <strong>จุดส่งเดียวกับ:</strong>
                  {{ visitMates(item).map(m => RJ.refText(m)).join(', ') || 'ไม่มีการจองอื่นในรายการนี้' }}
                  <v-btn x-small text color="teal darken-2" :disabled="!canManage(item) || hasPending(item)"
                         :loading="busyId === item.id" @click="doUnlinkVisit(item)">แยกจุดส่ง</v-btn>
                </div>
                <div v-for="c in visitOffers(item)" :key="'v' + c.id" class="caption mt-1">
                  ลูกค้าเดียวกันมีการจองวันนี้ช่วงเดียวกัน ({{ RJ.refText(c) }})
                  <v-btn x-small outlined color="teal darken-2" class="ml-1" :disabled="!canManage(item)"
                         :loading="busyId === item.id" @click="doLinkVisit(item, c)">รวมเป็นจุดส่งเดียวกัน</v-btn>
                </div>

                <div class="caption mt-1"><strong>ประวัติการเปลี่ยนแปลง:</strong></div>
                <!-- bookingHistory.timeline: the same lines ค้นหาการจอง prints,
                     requests and who approved them included. -->
                <div v-if="!historyLines(item).length" class="caption grey--text">ยังไม่มีประวัติ</div>
                <div v-for="(line, i) in historyLines(item)" :key="i" class="caption">• {{ line }}</div>
              </td>
            </template>
          </v-data-table>
          <!-- A shop's search here sees only its own bookings, so "not found" can
               mean "not yours". ค้นหาการจอง sees every booking; hand the same term
               over rather than leave the shop thinking the order does not exist. -->
          <v-alert v-if="rowsMode === 'search' && !rows.length && !listLoading && !canSeeAll"
                   dense text type="info" class="mt-3 mb-0">
            ไม่พบการจองของคุณ — ค้นหาทุกการจองได้ที่ ค้นหาการจอง
            <v-btn small text color="orange darken-2" class="ml-2" @click="searchEverywhere">
              ค้นหาทุกการจอง
            </v-btn>
          </v-alert>
        </v-card-text>
      </v-card>

      <!-- Below the list, not between the booking form and it: a shop books
           and looks its bookings up on every visit, while routes are
           delivery's occasional work. Collapsed by default for the same reason
           (see tripBoard.vue). -->
      <trip-board v-if="isStaff" :trips="trips" :strays="strayBookings" :can-manage="isStaff"
                  :plates="plates" @send="doSendTrip" @days="doTripDays"
                  @open="openTripDialog.open = true"
                  @close="doCloseTrip" @reopen="doReopenTrip" @cancelTrip="doCancelTrip"
                  @capacity="doSetTripCapacity" @attach="doAttachToTrip"
                  @addProvince="openAddProvince" @removeProvince="doRemoveProvince"
                  @pick="pickTripRows" />

      <!-- Deliberately not orange, and deliberately not red.

           Not orange: the two cards above are orange and this one was
           orange darken-2, the same toolbar as ค้นหา / จัดการการจอง just
           above it -- so the section that sets the fleet read as a third
           sibling of the two that handle one booking. It is not one. Those act
           on a booking; this decides how many jobs EVERY day can hold, across
           a date range, for everyone.

           Not red: everyone who can see this block is already staff
           (administrator / admin / manager / delivery -- see isStaffRole), and
           dispatch adjusts van counts as routine work. Warning colour on a
           daily task is how people learn to ignore warning colour. The problem
           to solve is category, not danger: this is settings, not a booking.

           The divider carries the same v-if as the card, or a non-staff login
           would see a heading introducing nothing. -->
      <template v-if="isStaff">
        <div class="d-flex align-center mt-6 mb-3">
          <v-divider style="max-width: 24px" />
          <span class="caption grey--text mx-3 text-no-wrap">สำหรับผู้ดูแลระบบ</span>
          <v-divider />
        </div>

        <!-- Collapsed by default: the fleet is set a few times a month, and an
             open form of eleven fields is a lot to scroll past for that. -->
        <v-card>
          <v-toolbar color="grey darken-3" dark dense flat class="section-toggle"
                     @click="capOpen = !capOpen">
            <v-icon class="mr-2">mdi-cog</v-icon>
            <v-toolbar-title>ตั้งค่าจำนวนรถ</v-toolbar-title>
            <v-spacer />
            <v-icon>{{ capOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-toolbar>
          <v-expand-transition>
          <v-card-text v-show="capOpen">
            <!-- Says what the colour can only hint at. -->
            <div class="caption grey--text mb-3">ตั้งค่านี้มีผลกับการจองของทุกคน</div>
            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field v-model="capForm.from" label="ตั้งแต่วันที่" type="date" color="orange accent-3" />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="capForm.to" label="ถึงวันที่" type="date" color="orange accent-3" />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field v-model.number="capForm.cars" label="จำนวนรถ" type="number" min="0"
                              color="orange accent-3" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="capForm.note" label="หมายเหตุ เช่น รถ 2ฒห-123 อุบัติเหตุ" clearable
                              color="orange accent-3" />
              </v-col>
            </v-row>
            <div class="caption grey--text mb-2">
              {{ capForm.cars || 0 }} คัน = ทั้งวัน/รวม {{ previewCaps.total }} · เช้า {{ previewCaps.am }} · บ่าย {{ previewCaps.pm }}
              (ข้ามวันอาทิตย์ · ใช้อัตรามาตรฐาน เช้า {{ standingPerCar.am }} · บ่าย {{ standingPerCar.pm }} · รวม {{ standingPerCar.total }} ต่อคัน)
            </div>
            <v-btn color="orange accent-3" dark :loading="savingCap" @click="saveCapacity">บันทึกช่วงวันที่</v-btn>
            <v-btn outlined color="orange darken-2" class="ml-2" :loading="savingCap"
                   @click="runRecount">คำนวณใหม่</v-btn>

            <v-divider class="my-4" />
            <div class="subtitle-2 mb-1">ค่ามาตรฐาน</div>
            <div class="caption grey--text mb-2">
              ตั้งค่าที่นี่ก่อน แล้วจึงบันทึกช่วงวันที่ด้านบน — วันที่บันทึกไว้จะเก็บอัตรานี้ไว้
              แม้จะแก้ค่ามาตรฐานภายหลัง
            </div>
            <v-row dense align="center">
              <v-col cols="6" md="3">
                <v-text-field v-model.number="defaultCars" label="จำนวนรถมาตรฐาน" type="number" min="0"
                              color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field v-model.number="defaultPerCar.am" label="งานต่อคัน (เช้า)" type="number" min="0"
                              color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field v-model.number="defaultPerCar.pm" label="งานต่อคัน (บ่าย)" type="number" min="0"
                              color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="6" md="2">
                <!-- Derived, never typed: the daily total is เช้า + บ่าย, so the three
                     numbers cannot be left disagreeing with each other. Styled
                     exactly like its neighbours -- `filled` boxed it and lifted its
                     baseline out of line, and a hint only this field carried made
                     the row taller than the other three. The caption below says
                     where the number comes from. -->
                <v-text-field :value="editingPerCar.total" label="งานต่อคัน (รวม)" readonly
                              class="derived-field" color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field v-model.number="defaultLeadDays" label="จองล่วงหน้า (วันทำการ)" type="number"
                              min="0" color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="6" md="2">
                <!-- The other end of the same window. Calendar days, not working
                     days: it is how far ahead the fleet is committed, and Sundays
                     are still days the operation has to plan around. -->
                <v-text-field v-model.number="defaultHorizonDays" label="เปิดจองล่วงหน้า (วัน)" type="number"
                              min="1" :max="maxHorizonDays" color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="12" md="3">
                <v-btn color="orange accent-3" dark :loading="savingCap"
                       @click="saveStandingConfig">บันทึกค่ามาตรฐาน</v-btn>
              </v-col>
            </v-row>
            <div class="caption grey--text mt-1">
              {{ defaultCars || 0 }} คัน × (เช้า {{ editingPerCar.am }} · บ่าย {{ editingPerCar.pm }} · รวม {{ editingPerCar.total }})
              = เช้า {{ standingCaps.am }} · บ่าย {{ standingCaps.pm }} · รวม {{ standingCaps.total }} ต่อวัน
              <span class="ml-1">(รวมต่อคันคำนวณอัตโนมัติจาก เช้า + บ่าย)</span>
              <div>{{ leadPreview }}</div>
              <div>{{ horizonPreview }}</div>
            </div>

          </v-card-text>
          </v-expand-transition>
        </v-card>
      </template>

      <!-- One dialog for all three actions: they share a subject, a permission
           rule and a history log. Only the middle section differs. -->
      <v-dialog v-model="dialog.open" max-width="560" persistent>
        <v-card>
          <v-toolbar :color="dialog.mode === 'cancel' ? 'error' : 'orange accent-3'" dark dense flat>
            <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pt-4">
            <div class="body-2 mb-2">
              {{ dialog.item.name || '-' }} · {{ dialog.item.telephone || '-' }}<br>
              {{ dialog.mode === 'move' ? 'เดิม: ' : '' }}{{ dialog.item.date }} ช่วง{{ labelTh[dialog.item.slot] }}
            </div>

            <v-row v-if="dialog.mode === 'move'" dense>
              <v-col cols="12" sm="6">
                <!-- Bounded for an owner move, open for staff -- the same split
                     the two alerts below describe, and the same one bookingService
                     applies. Without the min, the picker offered days the move
                     gate has to refuse. -->
                <v-text-field v-model="dialog.date" label="วันที่ใหม่" type="date"
                              :min="canSeeAll ? null : todayDate" :max="canSeeAll ? null : latest"
                              color="orange accent-3" hide-details="auto" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="dialog.slot" :items="slotOptions" label="ช่วงเวลาใหม่"
                          color="orange accent-3" hide-details="auto" />
              </v-col>
            </v-row>

            <v-alert v-if="dialog.mode === 'move' && !canSeeAll" dense text type="info" class="mt-3 mb-0">
              วันที่ใหม่ต้องมีที่ว่าง ไม่ใช่วันอาทิตย์ อยู่หลังวันสั่งซื้อ {{ configLeadDays }} วันทำการ และไม่เกินวันที่ {{ latest }}
            </v-alert>
            <v-alert v-else-if="dialog.mode === 'move'" dense text type="warning" class="mt-3 mb-0">
              เจ้าหน้าที่ย้ายได้ทุกวันโดยไม่ติดเงื่อนไข หากเกินจำนวนรถ ระบบจะแจ้งเตือนและทำเครื่องหมายวันนั้นไว้
              และหากย้ายไปวันที่ผ่านมาแล้ว ระบบจะให้ยืนยันอีกครั้ง
            </v-alert>

            <!-- แก้ไขข้อมูล — only what the customer supplies after booking.
                 Nothing here affects capacity, so no date, slot or SO. -->
            <div v-if="dialog.mode === 'edit'">
              <v-text-field v-model="dialog.fields.geo" label="พิกัด (ละติจูด, ลองจิจูด)"
                            :rules="[rules.geo]" clearable color="orange accent-3"
                            placeholder="13.7563, 100.5018" hide-details="auto">
                <template v-slot:append-outer>
                  <v-btn small color="orange accent-3" dark @click="openMapForDialog">
                    <v-icon left small>mdi-map-marker</v-icon>เปิดแผนที่
                  </v-btn>
                </template>
              </v-text-field>
              <v-text-field v-model="dialog.fields.landmark" label="จุดสังเกต" :counter="landmarkMax" :maxlength="landmarkMax" clearable
                            hint="เช่น บ้านหลังสีฟ้า ตรงข้ามร้านสะดวกซื้อ ปากซอยมีร้านกาแฟ"
                            persistent-hint color="orange accent-3" hide-details="auto" class="mt-3" />
              <!-- SAP holds a value this booking does not: the shop corrected
                   the order after the date was booked. Offered, never applied
                   on its own -- see applySapPin. -->
              <v-alert v-if="sapPinUpdate" dense text type="info" class="mt-3 mb-0">
                <div>ข้อมูลใน SAP ไม่ตรงกับการจองนี้</div>
                <div v-if="sapPinUpdate.geo" class="caption">พิกัดใน SAP: {{ sapPinUpdate.geo }}</div>
                <div v-if="sapPinUpdate.landmark" class="caption">จุดสังเกตใน SAP: {{ sapPinUpdate.landmark }}</div>
                <v-btn small text color="info" class="mt-1 px-1" @click="applySapPin">ใช้ค่าจาก SAP</v-btn>
              </v-alert>
              <div v-if="dialogProducts.length" class="mt-3">
                <div class="caption grey--text">สินค้าตาม SO (จาก SAP)</div>
                <div v-for="(p, i) in dialogProducts" :key="i" class="d-flex body-2">
                  <span class="flex-grow-1 mr-3">{{ productLabel(p) }}</span>
                  <span class="text-no-wrap">{{ productQty(p) || 1 }} ชิ้น</span>
                </div>
                <v-divider class="my-1" />
                <div class="caption grey--text d-flex">
                  <span class="flex-grow-1">รวม</span>
                  <span>{{ productTotal(dialogProducts) }} ชิ้น</span>
                </div>
              </div>
              <v-textarea v-model="dialog.fields.productList" label="รายการสินค้า" rows="1" auto-grow
                          clearable color="orange accent-3" hide-details="auto" class="mt-3" />
              <v-text-field v-model="dialog.fields.itemCount" label="จำนวนชิ้น" type="number" min="1"
                            clearable color="orange accent-3" hide-details="auto" class="mt-3"
                            hint="ใช้คำนวณเวลาติดตั้งและจัดรถ" persistent-hint />
              <!-- Correctable after booking for the same reason จำนวนชิ้น is: the tiers
                   are an average, and what a model actually takes is often learnt on
                   the first delivery of it. Clearing the field returns the job to the
                   calculated time. -->
              <v-text-field v-model="dialog.fields.installMinutes" label="เวลาติดตั้ง (นาที)"
                            type="number" :min="minInstallMinutes" :max="maxInstallMinutes" step="15"
                            clearable color="orange accent-3" hide-details="auto" class="mt-3"
                            :hint="dialogInstallHint" persistent-hint />

              <v-alert v-if="dialog.checkingDispatch" dense text type="info" class="mt-3 mb-0">
                กำลังตรวจสอบสถานะการจัดรถ...
              </v-alert>
              <v-alert v-else-if="dialog.dispatchWarn" dense text type="warning" class="mt-3 mb-0">
                {{ dialog.dispatchWarn }}
              </v-alert>
            </div>

            <!-- ใส่เลขที่ SO — the number a นัดหมาย was booked without, which
                 arrives one to three days before the delivery. The date and the
                 slot do not move; only the data behind them is replaced. -->
            <div v-if="dialog.mode === 'so'">
              <v-alert dense text type="info" class="mb-3">
                การจองนี้เป็นนัดหมาย (ยังไม่มีเลขที่ SO) เมื่อใส่เลขที่ SO ระบบจะนำข้อมูลลูกค้า ที่อยู่
                และรายการสินค้าจาก SO มาแทนที่ข้อมูลเดิม โดยวันและช่วงเวลาที่จองไว้ยังคงเดิม
              </v-alert>
              <v-text-field v-model="dialog.soNumber" label="เลขที่ SO" clearable
                            color="orange accent-3" hide-details="auto"
                            @keydown.enter="lookupSoForBooking">
                <template v-slot:append-outer>
                  <v-btn small color="orange accent-3" dark :loading="dialog.lookingUp"
                         @click="lookupSoForBooking">
                    <v-icon left small>mdi-magnify</v-icon>ค้นหา
                  </v-btn>
                </template>
              </v-text-field>

              <!-- What will change, before it changes. Shown from the same diff
                   attachSo writes to the history, so the two cannot disagree. -->
              <div v-if="dialog.soFound" class="mt-4">
                <div class="caption grey--text">ข้อมูลที่จะเปลี่ยนตาม SO {{ dialog.soFound.soNo }}</div>
                <div v-if="!dialog.soChanges.length" class="body-2 mt-1">
                  ข้อมูลตรงกับที่จองไว้แล้ว — จะบันทึกเฉพาะเลขที่ SO
                </div>
                <div v-for="c in dialog.soChanges" :key="c.field" class="body-2 mt-1">
                  <strong>{{ fieldLabel(c.field) }}</strong> :
                  <span class="grey--text">{{ changeValue(c.from) }}</span>
                  <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                  {{ changeValue(c.to) }}
                </div>
              </div>
            </div>

            <v-select v-if="dialog.mode === 'move' || dialog.mode === 'cancel'"
                      v-model="dialog.reason" :items="moveReasons"
                      label="เหตุผล" class="mt-3" color="orange accent-3" hide-details="auto" />
            <v-text-field v-if="dialog.mode === 'move' || dialog.mode === 'cancel'"
                          v-model="dialog.note"
                          label="หมายเหตุเพิ่มเติม (ถ้ามี)" clearable
                          color="orange accent-3" hide-details="auto" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="dialog.saving" @click="dialog.open = false">ปิด</v-btn>
            <v-btn :color="dialog.mode === 'cancel' ? 'error' : 'orange accent-3'" dark
                   :disabled="dialog.mode === 'so' && !dialog.soFound"
                   :loading="dialog.saving" @click="saveDialog">{{ dialogAction }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="openTripDialog.open" max-width="480">
        <v-card>
          <v-toolbar color="deep-purple" dark dense flat>
            <v-toolbar-title class="subtitle-1">เปิดเส้นทางนอกพื้นที่</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pt-4">
            <v-text-field v-model="openTripDialog.date" label="วันที่" type="date"
                          color="deep-purple" hide-details="auto" class="mb-3" />
            <!-- Several provinces are one route because ราชบุรี and สมุทรสงคราม
                 are adjacent and are one drive. Matching stays automatic; the
                 judgement about what counts as one drive stays with delivery.

                 An autocomplete, and deliberately neither of the two obvious
                 alternatives. A combobox takes free text, and one typo opens a
                 route to a province name R.provinceOf can never match -- the
                 route is then invisible to every address it was meant to
                 serve. A plain select fixes that but leaves 77 provinces to
                 scroll through to reach ราชบุรี. This constrains the value to
                 a province that really exists AND lets it be typed. -->
            <v-autocomplete v-model="openTripDialog.provinces" label="จังหวัดปลายทาง"
                            :items="provinceOptions" :loading="!zoneDv"
                            multiple chips deletable-chips color="deep-purple"
                            no-data-text="ไม่พบจังหวัดนี้"
                            hint="พิมพ์เพื่อค้นหา · เลือกได้มากกว่าหนึ่งจังหวัดถ้าไปเส้นทางเดียวกัน เช่น ราชบุรี + สมุทรสงคราม"
                            persistent-hint class="mb-3" />
            <v-row dense>
              <v-col cols="6">
                <v-text-field v-model.number="openTripDialog.capacity" label="รับได้กี่งาน"
                              type="number" min="1" color="deep-purple" hide-details="auto" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model.number="openTripDialog.cars" label="จองรถกี่คัน"
                              type="number" min="1" color="deep-purple" hide-details="auto" />
              </v-col>
            </v-row>
            <v-alert dense text type="warning" class="caption mt-3 mb-0">
              รถที่จองไว้จะถูกหักออกจากจำนวนงานที่รับได้ตามปกติของวันนั้น
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="openTripDialog.open = false">ยกเลิก</v-btn>
            <v-btn color="deep-purple" dark :loading="openTripDialog.saving" @click="doOpenTrip">
              เปิดเส้นทาง
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Extending a route that is already open: delivery decides ราชบุรี on
           one day and learns on the next that กาญจนบุรี is on the way. -->
      <v-dialog v-model="addProvinceDialog.open" max-width="480">
        <v-card>
          <v-toolbar color="deep-purple" dark dense flat>
            <v-toolbar-title class="subtitle-1">เพิ่มจังหวัดในเส้นทาง</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pt-4">
            <div class="mb-3">
              {{ addProvinceDialog.date }} · {{ addProvinceDialog.current.join(', ') }}
              · {{ addProvinceDialog.count }}/{{ addProvinceDialog.capacity }} งาน
            </div>
            <v-autocomplete v-model="addProvinceDialog.provinces" label="จังหวัดที่เพิ่ม"
                            :items="addProvinceOptions" :loading="!zoneDv"
                            multiple chips deletable-chips color="deep-purple"
                            no-data-text="ไม่พบจังหวัดนี้" hide-details="auto" class="mb-3" />
            <v-alert dense text type="info" class="caption mb-0">
              ใช้รถคันเดิมและจำนวนงานเดิม ร้านค้าจะจองจังหวัดที่เพิ่มในเส้นทางนี้ได้ทันที
              ถ้างานมากขึ้น ใช้ปุ่มแก้จำนวนงาน
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="addProvinceDialog.open = false">ยกเลิก</v-btn>
            <v-btn color="deep-purple" dark :disabled="!addProvinceDialog.provinces.length"
                   :loading="addProvinceDialog.saving" @click="doAddProvince">เพิ่มจังหวัด</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="stopDialog.open" max-width="440">
        <v-card>
          <v-toolbar :color="stopDialog.stopped ? 'green' : 'red'" dark dense flat>
            <v-toolbar-title class="subtitle-1">
              {{ stopDialog.stopped ? 'เปิดรับจอง' : 'ปิดรับจอง' }} วันที่ {{ stopDialog.label }}
            </v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pt-4">
            <div v-if="stopDialog.counts" class="mb-3">
              จองแล้ว {{ stopDialog.counts.total }} จาก {{ stopDialog.caps.total }} งาน
            </div>
            <template v-if="!stopDialog.stopped">
              <div class="mb-3">
                ร้านค้าจะจองหรือย้ายการจองมาวันนี้ไม่ได้ การจองที่มีอยู่แล้วไม่เปลี่ยนแปลง
                และเจ้าหน้าที่ยังจองได้ เส้นทางนอกพื้นที่ของวันนี้ไม่ได้รับผลกระทบ ใช้ปุ่มปิดรับของเส้นทางแทน
              </div>
              <v-text-field v-model="stopDialog.note" color="red"
                            :label="stopDialog.noteRequired ? 'เหตุผล (จำเป็น)' : 'เหตุผล (ไม่บังคับ)'"
                            placeholder="เช่น งานเต็มกำลังทีมติดตั้ง" counter="80" maxlength="80"
                            hide-details="auto" />
            </template>
            <div v-else>ร้านค้าจะกลับมาจองวันนี้ได้ตามจำนวนที่ว่าง</div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="stopDialog.open = false">ยกเลิก</v-btn>
            <v-btn :color="stopDialog.stopped ? 'green' : 'red'" :dark="!stopNoteMissing" :loading="stopDialog.saving"
                   :disabled="stopNoteMissing" @click="saveStopDialog">
              {{ stopDialog.stopped ? 'เปิดรับจอง' : 'ปิดรับจอง' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="sundayDialog.open" max-width="440">
        <v-card>
          <v-toolbar color="teal" dark dense flat>
            <v-toolbar-title class="subtitle-1">เปิดรับจองวันอาทิตย์ {{ sundayDialog.label }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pt-4">
            <div class="mb-3">
              ร้านค้าและเจ้าหน้าที่จะจองวันนี้ได้เหมือนวันทำการ ตามจำนวนรถที่ระบุ
              หากต้องการหยุดรับภายหลัง ใช้ปุ่มปิดรับจองของวันนี้ (ต้องระบุเหตุผล)
            </div>
            <v-text-field v-model="sundayDialog.cars" type="number" min="1" label="จำนวนรถที่ออกวันนี้ (คัน)"
                          color="teal" hide-details="auto" class="mb-3" />
            <v-text-field v-model="sundayDialog.note" label="หมายเหตุ (ไม่บังคับ)" color="teal"
                          placeholder="เช่น งานสัปดาห์นี้เต็ม" counter="80" maxlength="80" hide-details="auto" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="sundayDialog.open = false">ยกเลิก</v-btn>
            <v-btn color="teal" dark :loading="sundayDialog.saving" @click="saveSundayDialog">เปิดรับจอง</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios'
import moment from 'moment-timezone'
import R from '@/components/bookingRules'
import X from '@/components/proposalRules'
import H from '@/components/bookingHistory'
import O from '@/components/orderSearch'
import { pushPinToSap, pushBookingPinToSap, sapFailedNote } from '@/components/sapPin'
import { pushBookDateToSap, bookDateFailedNote } from '@/components/sapBookDate'
import P from '@/components/people'
import S from '@/components/bookingService'
import V from '@/components/visitRules'
import I from '@/components/installTime'
import config from '@/components/config'
import { reportWarning, reportError } from '@/components/report'
import RJ from '@/components/returnJob'

// How long the page waits before following a prefilled นัดหมาย back to the
// system that handed it over. Long enough to read the line and press ยกเลิก,
// short enough that the common case needs no decision at all.
const APPT_RETURN_SECONDS = 8
import { isServiceable, isServiceableLocation, isFullyServedProvince, locationArea, areaLabel, resolveFromAddress } from '@/components/serviceArea'
import mainMenu from '@/components/mainMenu'
import D from '@/components/dispatchService'
import F from '@/components/fleet'
import tripBoard from '@/components/tripBoard'
import proposalPanel from '@/components/proposalPanel'
import U from '@/components/upcountry'
import US from '@/components/upcountryService'
import upcountryBookingDialog from '@/components/upcountryBookingDialog'
import upcountryOrderActions from '@/components/upcountryOrderActions'

export default {
  name: 'bookingDate',
  components: { mainMenu, tripBoard, proposalPanel, upcountryBookingDialog, upcountryOrderActions },
  data () {
    return {
      // SAP's U_KSK_Landmark width -- จุดสังเกต is written back to it.
      landmarkMax: R.SAP_LANDMARK_MAX,
      mode: 'so',
      // This form arrived prefilled from another system (?appt=1). Shown once,
      // so the person knows the customer half was not typed here and still has
      // to be checked before it becomes a promise to a customer.
      apptHandover: false,
      // Where this prefilled นัดหมาย came from: the other system's order number
      // (stored on the booking as sourceRef) and an allowlisted URL to report
      // the result back to. Null for every ordinary booking.
      apptReturn: null,
      // The ตำบล the other system named for this handover (R.apptLocation), as
      // the ตำบล list's value. Only a wish: loadSubDistricts selects it when the
      // zipcode's list offers it, and otherwise it is picked by hand.
      apptLocation: '',
      // What became of the handover once the booking was made: where it went,
      // whether the other tab was told directly, and the countdown before this
      // page follows it. Kept until the NEXT booking starts, so cancelling the
      // return leaves the way back on screen rather than losing it.
      apptResult: null,
      apptTimer: null,
      soNumber: '',
      // The รับคืน tab's own number. The exchange SO is found through GetSoByRt
      // (an RT's own soNo is the original sale and only displayed); GetSo is
      // never asked for this number.
      rtNumber: '',
      // { soNo, rtNo }: the RT typed on the รับคืน tab that steered (BOOK_SO) to
      // this SO. fetchRtForSo prefers it over the SO's own U_KSK_ReturnNo and
      // over the original sale's newest RT, so the hop cannot swap the RT the
      // person typed for a guess. Cleared by resetJob.
      steeredRt: null,
      soData: null,
      // The return the looked-up SO carries (fetchRtForSo): { rt, rows, fields,
      // note } when it books as an exchange, { note } when it does not, else null.
      rtData: null,
      // What resolveFromAddress made of the SO's address. Kept because it is
      // the only ตำบล this path ever learns -- SAP sends one free-text line and
      // there is no picker behind it.
      soArea: null,
      // The SO's product lines exactly as GetProdList returns them. Stored with
      // the booking because carBooking's appointment table renders รหัสสินค้า,
      // สีหน้าบาน|โครงตู้ and จำนวน straight out of these rows, and an appointment
      // made here had nothing in any of those columns.
      soProducts: [],
      loadingSo: false,
      orderDate: R.todayStr(),
      form: R.blankBookingForm(),
      subDistrictOptions: [],
      loadingZones: false,
      zoneDv: null,                    // zonedv.json, fetched on demand (see loadSubDistricts)
      rules: {
        required: v => !!v || 'จำเป็นต้องกรอก',
        zipCode: v => !v || /^\d{5}$/.test(v) || 'รหัสไปรษณีย์ต้องมี 5 หลัก',
        // A phone saved in any other shape cannot be found again on ค้นหาการจอง.
        tel: v => R.telProblem(v) || true,
        geo: v => !v || !!R.parseGeo(v) || 'รูปแบบพิกัดไม่ถูกต้อง เช่น 13.7563, 100.5018'
      },
      slots: R.SLOTS,
      labelTh: R.SLOT_LABEL_TH,
      monthStart: R.todayStr().slice(0, 7) + '-01',
      byDate: {},
      configCars: R.DEFAULT_CARS,
      selected: { date: '', slot: '' },
      submitting: false,
      unsubMonth: null,
      busyId: '',
      savingCap: false,
      capOpen: false,
      upOrdersOpen: false,
      defaultCars: R.DEFAULT_CARS,
      defaultPerCar: Object.assign({}, R.PER_CAR),
      defaultLeadDays: R.LEAD_WORKING_DAYS,
      configLeadDays: R.LEAD_WORKING_DAYS,
      defaultHorizonDays: R.BOOKING_HORIZON_DAYS,
      configHorizonDays: R.BOOKING_HORIZON_DAYS,
      // Up-country (large truck) booking dialog.
      U,
      RJ,
      upDialog: false,
      upFields: null,
      upProvince: '',
      configPerCar: Object.assign({}, R.PER_CAR),
      capForm: { from: R.todayStr(), to: R.todayStr(), cars: R.DEFAULT_CARS, note: '' },
      // The booking list serves two jobs from one table: the work view by
      // default, and the result of a support search when one is run.
      //
      // `date` is the work view's day. It opens on today because that is the
      // day being dispatched; staff move it forward to plan ahead, or back to
      // see what yesterday held.
      itemCountFromSo: false,
      search: { tel: '', soNo: '', date: R.todayStr() },
      // 'day' shows search.date alone; 'ahead' shows the fortnight starting
      // there. One day is the default because a staff login sees EVERY booking
      // -- a fortnight of those is hundreds of rows. A shop login is scoped to
      // its own bookings, which is a handful, so 'ahead' is the useful view
      // there.
      rangeMode: 'day',
      // How wide 'ahead' is. Read by both the span and the button that offers
      // it, so the label cannot come to disagree with what it shows.
      aheadDays: 14,
      searching: false,
      // True from asking for a range until its first snapshot lands. Changing
      // the day re-queries, and for that round trip the caption would otherwise
      // describe the new range while the table still showed the old one's rows
      // -- stating a count it had not loaded.
      listLoading: false,
      rows: [],
      rowsMode: 'list',
      // A group supervisor's นัดหมายรอ SO list: every live นัดหมาย, narrowed
      // to the group by awaitingSoRows. Subscribed only while isSupervisor.
      awaitingSo: [],
      unsubAwaiting: null,
      // Every live up-country order (US.subscribeOrders, the same snapshot the
      // booking dialog opens); narrowed to this login's own by myUpOrders.
      upOrders: [],
      unsubUpOrders: null,
      // Routes replaced the loose out-of-area rows PR #36 listened for -- see
      // watchTrips. This is for the board, which needs the trip documents
      // themselves (history, who opened them, cars). Staff only -- nobody
      // else can open or see a route.
      trips: [],
      unsubTrips: null,
      // Plate strings for the ส่งเข้าคิวจัดรถ dialog. Loaded once, lazily, when
      // the board first needs them -- a shop never opens the board and should
      // not pay for the read.
      plates: [],
      openTripDialog: { open: false, date: R.todayStr(), provinces: [], capacity: 5, cars: 1, saving: false },
      addProvinceDialog: { open: false, tripId: '', date: '', current: [], count: 0, capacity: 0, provinces: [], saving: false },
      stopDialog: { open: false, date: '', label: '', stopped: false, note: '', noteRequired: false, counts: null, caps: null, saving: false },
      sundayDialog: { open: false, date: '', label: '', cars: 0, note: '', saving: false },
      expanded: [],
      unsubList: null,
      // `item` starts as {} so the dialog template can read item.name before
      // anything has been selected.
      dialog: {
        open: false,
        mode: 'move',                  // 'move' | 'cancel' | 'edit' | 'so'
        item: {},
        date: '',
        slot: '',
        reason: '',
        note: '',
        fields: { geo: '', landmark: '', productList: '', itemCount: '', installMinutes: '' },
        // The attach path. soFound is what the SO lookup returned and what the
        // save writes; until it is set there is nothing to save, which is what
        // stops a number being stored that nobody has checked against SAP.
        soNumber: '',
        soFound: null,
        soChanges: [],
        lookingUp: false,
        dispatchWarn: '',
        checkingDispatch: false,
        // พิกัด / จุดสังเกต as SAP holds them, read with the dispatch check.
        sapPin: null,
        saving: false
      },
      bookingHeaders: [
        { text: 'วันที่', value: 'date' },
        { text: 'ช่วง', value: 'slot' },
        { text: 'SO', value: 'soNo' },
        { text: 'ลูกค้า', value: 'name' },
        { text: 'พิกัด', value: 'geo', sortable: false, align: 'center' },
        { text: 'โทร', value: 'telephone' },
        { text: 'สถานะ', value: 'status' },
        { text: '', value: 'action', sortable: false }
      ]
    }
  },
  watch: {
    // The directory arrives after the page does, and an administrator can make
    // someone a supervisor (or not) while they have it open.
    isSupervisor: {
      immediate: true,
      handler (yes) {
        if (yes) this.watchAwaitingSo()
        else this.stopAwaitingSo()
      }
    },
    'openTripDialog.open' (isOpen) {
      // perCar.total is a standing setting, not a constant, so the default is
      // read when the dialog opens rather than baked into data().
      if (isOpen) this.openTripDialog.capacity = R.normalizePerCar(this.configPerCar).total
      // The province list comes out of zonedv.json, which this page loads on
      // demand. Opening the dialog is the first thing that needs it when nobody
      // has typed a zipcode yet, so ask for it here rather than eagerly.
      if (isOpen) this.ensureZoneDv()
    },
    // The month the grid opens on has to be a month that has days in it. It is
    // seeded from today, but the lead time can push the first bookable day into
    // next month -- on the 30th with four working days it is already October --
    // and bookingConfig, which sets that lead time, arrives after the page does.
    // So re-anchor onto the month holding `earliest`, but only from a month that
    // offers nothing: a staff member who paged forward deliberately is left
    // where they are. watchMonth() re-points the Firestore listener at the new
    // month; it drops the old one first, so calling it here is safe.
    earliest: {
      immediate: true,
      handler (first) {
        if (!first || this.days.length) return
        const anchor = moment.tz(first, R.TZ).format('YYYY-MM-01')
        if (anchor === this.monthStart) return
        this.monthStart = anchor
        this.watchMonth()
      }
    },
    'form.zipcode' (zip) { this.loadSubDistricts(zip) },
    // Pushing the start past the end leaves a range that saves nothing, so the
    // end follows it up to meet it. Only this direction: dragging the END back
    // before the start would have to move the start backwards to compensate,
    // which silently extends the range into days the user never looked at.
    // That case stays a validation message instead.
    // ISO dates compare lexicographically, which is also chronologically.
    'capForm.from' (from) {
      if (from && this.capForm.to && this.capForm.to < from) this.capForm.to = from
    },
    // The date anchors whichever width is selected, so naming one slides the
    // window rather than changing what kind of window it is. Cleared back to
    // empty it falls to today rather than querying an empty range.
    'search.date' (date) {
      if (!date) { this.search.date = R.todayStr(); return }
      this.showList()
    },
    rangeMode () { this.showList() },
    // The directory arrives after the first list snapshot. A converted shop's
    // older bookings carry its previous email, so the list re-subscribes once
    // the full identity list is known rather than waiting for a day change.
    myIdentities (now, before) {
      if (this.canSeeAll || String(now) === String(before)) return
      if (this.rowsMode !== 'search') this.showList()
    },
    // The date and the width above put the table back on a day when they
    // change; the search boxes did not, and that asymmetry was the bug.
    // Emptying the last term is the same request as ล้างตัวกรอง -- the controls
    // have stopped describing a search -- but nothing observed it, so rowsMode
    // stayed 'search' and the day's live snapshots kept being discarded on
    // purpose while the table sat on a result nothing on screen explained.
    'search.tel' () { this.listWhenSearchEmptied() },
    'search.soNo' () { this.listWhenSearchEmptied() },
    // Switching มีเลขที่ SO <-> นัดหมาย starts a different job, so it clears the
    // last one -- the same pair the page runs after a successful booking, which
    // is the other place a job ends.
    //
    // Nothing did this before, and the form hid how much was left behind: the SO
    // path binds only พิกัด / จุดสังเกต / รายการสินค้า / จำนวนชิ้น / เวลาติดตั้ง to
    // the form, so those were the visible leak -- but soData stayed loaded too,
    // and createBooking reads custName / custTel / custAddress / city / cardCode
    // straight off it with no form fallback. A นัดหมาย confirmed after a lookup
    // was therefore filed against the previous SO's customer, with nothing on
    // screen saying so.
    // The up-country dialog took the looked-up job (soData stays unset); once
    // it closes, saved or not, the return it carried belongs to nothing on screen.
    upDialog (open) {
      if (!open) this.rtData = null
    },
    mode () {
      this.resetJob()
      this.resetForm()
      // The day chosen for the old job is not a choice about the new one.
      this.selected = { date: '', slot: '' }
    }
  },

  computed: {
    // A Sunday may be closed only with a reason (R.stopNoteRequired); the
    // service refuses an empty one too.
    stopNoteMissing () {
      return this.stopDialog.noteRequired && !String(this.stopDialog.note || '').trim()
    },
    webPath () { return this.$store.getters.webPath },
    // A SAP document (an SO, or on the รับคืน tab an RT) supplied the customer:
    // name, phone and address are read-only and come from soData.
    fromSap () { return this.mode === 'so' || this.mode === 'rt' },
    // The return half of an exchange, one line per RT item.
    rtLines () { return this.rtData && this.rtData.fields ? RJ.returnLines(this.rtData.fields.prodListRt) : [] },
    // Non-null when SAP carries a พิกัด or จุดสังเกต this booking does not --
    // typically a shop that corrected the order after booking the date.
    sapPinUpdate () { return R.sapPinUpdate(this.dialog.fields, this.dialog.sapPin) },
    // Shows what the free-text รายการสินค้า reads as, so staff can see the number
    // the planner will use and correct it before booking rather than after.
    itemCountHint () {
      // With a duration set by hand the tiers no longer decide anything, so the
      // count stops advertising minutes -- two different durations on screen at
      // once is worse than one, whichever of them staff happen to read.
      const timed = this.installOverride != null
      const typed = Math.floor(Number(this.form.itemCount))
      if (isFinite(typed) && typed > 0) {
        return timed ? `${typed} ชิ้น` : `ติดตั้งประมาณ ${I.installMinutes(typed)} นาที`
      }
      const guess = I.countFromProductList(this.form.productList)
      if (guess.items > 0) {
        return `อ่านจากรายการได้ ${guess.items} ชิ้น` +
          (timed ? '' : ` (~${I.installMinutes(guess.items)} นาที)`) +
          (guess.confident ? '' : ' — โปรดตรวจสอบ')
      }
      return `ไม่ระบุ จะใช้ค่าเริ่มต้น ${I.DEFAULT_ITEMS} ชิ้น`
    },

    // The SAP lines stored with the booking. Present only for one made against
    // an SO after prodList started being stored; an older row, a นัดหมาย, or an
    // SO whose GetProdList call failed has nothing here and falls back to the
    // free text underneath.
    dialogProducts () {
      const item = this.dialog.item || {}
      return Array.isArray(item.prodList) ? item.prodList : []
    },

    minInstallMinutes () { return I.MIN_INSTALL_MINUTES },
    maxInstallMinutes () { return I.MAX_INSTALL_MINUTES },

    // The typed duration as a number, or null when the field is empty, zero or
    // nonsense -- exactly the reading minutesForBooking gives it, so what the
    // hint promises and what the planner does cannot drift apart.
    installOverride () {
      const typed = Math.floor(Number(this.form.installMinutes))
      return isFinite(typed) && typed > 0 ? typed : null
    },

    installOutOfRange () {
      const n = this.installOverride
      return n != null && (n < I.MIN_INSTALL_MINUTES || n > I.MAX_INSTALL_MINUTES)
    },

    // What the tiers would say, ignoring any override -- the number staff are
    // choosing to overrule, and the one they get back by clearing the field.
    derivedInstallMinutes () {
      return I.minutesForBooking({
        itemCount: this.form.itemCount,
        productList: this.form.productList
      }).minutes
    },

    // The dialog edits a booking that already exists, so its calculated value
    // comes from the stored count rather than the form's.
    dialogInstallHint () {
      const typed = Math.floor(Number(this.dialog.fields.installMinutes))
      const derived = I.minutesForBooking({
        itemCount: this.dialog.fields.itemCount,
        productList: this.dialog.fields.productList
      }).minutes
      if (isFinite(typed) && typed > 0) {
        if (typed < I.MIN_INSTALL_MINUTES || typed > I.MAX_INSTALL_MINUTES) {
          return `ต้องอยู่ระหว่าง ${I.MIN_INSTALL_MINUTES}-${I.MAX_INSTALL_MINUTES} นาที`
        }
        return `กำหนดเอง — ค่าคำนวณคือ ${derived} นาที (ล้างช่องเพื่อใช้ค่าคำนวณ)`
      }
      return `ใช้ค่าคำนวณ ${derived} นาที`
    },

    installMinutesHint () {
      if (this.installOutOfRange) {
        return `ต้องอยู่ระหว่าง ${I.MIN_INSTALL_MINUTES}-${I.MAX_INSTALL_MINUTES} นาที`
      }
      if (this.installOverride != null) {
        return `กำหนดเอง — ค่าคำนวณคือ ${this.derivedInstallMinutes} นาที (ล้างช่องเพื่อใช้ค่าคำนวณ)`
      }
      return `ใช้ค่าคำนวณ ${this.derivedInstallMinutes} นาที`
    },
    locationHint () {
      if (!/^\d{5}$/.test(this.form.zipcode || '')) return 'กรอกรหัสไปรษณีย์ก่อน'
      if (this.loadingZones) return 'กำลังค้นหา...'
      if (!this.subDistrictOptions.length) return 'ไม่พบรหัสไปรษณีย์นี้'
      // Only staff are ever offered one, so only staff can ever read this.
      if (locationArea(this.form.location) === 'out') return 'ตำบลนี้อยู่นอกพื้นที่จัดส่งปกติ'
      return ''
    },
    myEmail () { return this.$store.state.user || '' },
    // Every email this account has had: its bookings may have been made under
    // an old one (people.ownedBy).
    myIdentities () { return this.$store.getters.identities },
    myUpOrders () { return U.myOrders(this.upOrders, this.myIdentities, R.todayStr()) },
    // One role list behind three powers: set fleet capacity, see every booking,
    // and move any of them past the ordinary rules. Everyone else sees and moves
    // only what their own login created. Enforced again in
    // bookingService.moveBooking -- this only decides what the page draws.
    isStaff () { return R.isStaffRole(this.$store.state.status) },
    canSeeAll () { return this.isStaff },
    // What X.canAttachSo reads: the supervisor case needs the caller's and the
    // owner's userProfile, both in the directory App.vue keeps in the store.
    attachCtx () {
      return {
        role: this.$store.state.status,
        identities: this.myIdentities,
        user: this.myEmail,
        profiles: this.$store.state.profiles
      }
    },
    myProfile () { return X.profileOf(this.$store.state.profiles, this.myEmail) },
    // Staff already see and attach to every booking in the list below, so the
    // นัดหมายรอ SO card is for a supervisor shop login only.
    isSupervisor () {
      const p = this.myProfile
      return !this.isStaff && !!p && p.supervisor === true && !!p.bookingGroup
    },
    awaitingSoRows () {
      return this.awaitingSo.filter(b => X.canAttachSo(b, this.attachCtx))
    },
    slotOptions () {
      return R.SLOTS.map(s => ({ text: R.SLOT_LABEL_TH[s] + ' ' + R.SLOT_TIME[s], value: s }))
    },
    moveReasons () { return R.MOVE_REASONS },
    dialogTitle () {
      return { move: 'ย้ายวันจัดส่ง', cancel: 'ยกเลิกการจอง', edit: 'แก้ไขข้อมูลการจอง', so: 'ใส่เลขที่ SO' }[this.dialog.mode]
    },
    dialogAction () {
      return { move: 'ย้ายวัน', cancel: 'ยืนยันการยกเลิก', edit: 'บันทึกการแก้ไข', so: 'บันทึกเลขที่ SO' }[this.dialog.mode]
    },
    // Both views are the same range query; only the width differs. The date
    // field anchors BOTH of them: it says where the list starts, the toggle says
    // how wide it is. Anchoring the fortnight to today instead left the field
    // showing a date the list was ignoring.
    listFrom () { return this.search.date },
    listTo () {
      return this.rangeMode === 'ahead'
        ? moment.tz(this.search.date, R.TZ).add(this.aheadDays, 'day').format('YYYY-MM-DD')
        : this.search.date
    },
    // How the day divides between the two windows -- which is what says how many
    // cars it needs. Counted from the rows already on screen, so it costs
    // nothing, and it is deliberately absent from the 14-day view, where a sum
    // across a fortnight would mean nothing.
    daySlotCounts () {
      const c = { full: 0, am: 0, pm: 0 }
      this.rows.forEach(r => { if (c[r.slot] != null) c[r.slot]++ })
      return c
    },
    // "ไม่มีการจองในช่วงนี้" under a one-day view reads as though a range were
    // being shown, which is the one thing the day view is not.
    emptyText () {
      if (this.rowsMode === 'search') return 'ไม่พบการจองที่ตรงกัน'
      if (this.rangeMode === 'ahead') return 'ไม่มีการจองในช่วงนี้'
      return `ไม่มีการจองในวันที่ ${this.search.date}`
    },
    // Live bookings a driver would have to navigate to by address alone.
    missingPinCount () {
      return this.rows.filter(r => this.isLive(r) && !this.hasPin(r)).length
    },
    listCaption () {
      if (this.rowsMode === 'search') return `ผลการค้นหา ${this.rows.length} รายการ`
      // Both ends are named, because the span no longer starts anywhere the
      // reader could assume. "ตั้งแต่วันนี้" is kept for the one case where
      // they could -- the default, anchored on today.
      const what = this.rangeMode !== 'ahead'
        ? `การจองวันที่ ${this.search.date}`
        : this.listFrom === R.todayStr()
          ? `การจองตั้งแต่วันนี้ถึง ${this.listTo}`
          : `การจอง ${this.listFrom} ถึง ${this.listTo}`
      // The rows still on screen belong to the PREVIOUS range until the first
      // snapshot of this one lands, so counting them here would attach the old
      // day's number to the new day's name.
      if (this.listLoading) return `${what} · กำลังโหลด...`
      // A customer booked twice on one day is one stop, and the day's seats
      // count stops -- said beside the number of bookings when the two differ.
      const stops = V.stopCount(this.rows)
      const live = this.rows.filter(b => R.isActive(b.status)).length
      const head = `${what} · ${this.rows.length} รายการ` + (stops < live ? ` · ${stops} จุดส่ง` : '')
      if (this.rangeMode === 'ahead' || !this.rows.length) return head
      const c = this.daySlotCounts
      return `${head} · ทั้งวัน ${c.full} · เช้า ${c.am} · บ่าย ${c.pm}`
    },
    // The range editor never asks for a rate; it stamps the standing one. So the
    // preview has to show the standing rate too, or it would promise capacity
    // the save will not produce.
    standingPerCar () { return R.normalizePerCar(this.configPerCar) },
    // What the form currently says, before it is saved -- so the line under the
    // inputs describes the change being made, not the one already stored.
    editingPerCar () { return R.withDerivedTotal(this.defaultPerCar) },
    standingCaps () { return R.capacityForCars(this.defaultCars, this.editingPerCar) },
    previewCaps () { return R.capacityForCars(this.capForm.cars, this.configPerCar) },
    // firstBookableDate, not earliestDeliveryDate: an old SO would compute a
    // lead date already in the past, and the grid is clipped to this.
    earliest () { return R.firstBookableDate(this.orderDate, this.configLeadDays) },
    todayDate () { return R.todayStr() },
    // Shows the rule in the only terms that matter: the date it produces today.
    leadPreview () {
      const n = R.normalizeLeadDays(this.defaultLeadDays)
      const first = R.earliestDeliveryDate(R.todayStr(), n)
      return n === 0
        ? `จองล่วงหน้า 0 วันทำการ — สั่งวันนี้ส่งได้ตั้งแต่วันนี้ (${first})`
        : `จองล่วงหน้า ${n} วันทำการ — สั่งวันนี้ส่งได้ตั้งแต่ ${first}`
    },
    // The far end of the bookable window, on the same footing as `earliest`.
    latest () { return R.horizonDate(R.todayStr(), this.configHorizonDays) },
    maxHorizonDays () { return R.MAX_HORIZON_DAYS },
    // leadPreview's mirror at the other end: the setting is a count of days, and
    // the only thing anyone reads off it is the date it produces today.
    horizonPreview () {
      const n = R.normalizeHorizonDays(this.defaultHorizonDays)
      return `เปิดจองล่วงหน้า ${n} วัน — จองได้ถึง ${R.horizonDate(R.todayStr(), n)}`
    },
    monthLabel () { return moment.tz(this.monthStart, R.TZ).format('MMMM YYYY') },
    canSubmit () {
      if (!this.selected.date || !this.selected.slot) return false
      // A shop that got this far without a route is a stale form. The
      // transaction would refuse it; refusing it here says why.
      //
      // Only a route that can actually take this booking. 'full' and 'closed'
      // are refusals too -- letting them through is what let a shop write a
      // booking onto a route with no seat for it.
      if (this.bookingOutOfArea &&
          ['book', 'openNew'].indexOf(this.bookingTripGate) === -1) return false
      if (this.fromSap) return !!this.soData
      // isServiceableLocation is the last gate rather than the only one: the ตำบล
      // list is already filtered, so this only catches a location that arrived
      // some other way (a stale form, a pasted value). Staff are past it on
      // purpose -- areaGateFor says they may book outside the routine area, and
      // the ตำบล list they were offered says which ตำบล those are.
      return !!(this.form.name && this.form.telephone && this.form.address &&
                this.form.zipcode && this.form.location &&
                (this.isStaff || isServiceableLocation(this.form.location)))
    },
    // Whether the booking about to be made is a far-province trip, derived the
    // same way bookingService.createBooking derives what it stores -- the
    // location string first, and only where that says nothing does the SO's
    // resolved address (which staff have already confirmed) decide. Two
    // different answers here and there would show one thing on the calendar and
    // count another.
    bookingOutOfArea () {
      const area = locationArea(this.bookingLocation)
      if (area !== 'unknown') return area === 'out'
      // Only reachable on the SO path, whose address may name no single ตำบล.
      // The appointment path cannot get here: its ตำบล is required.
      return !!(this.soArea && this.soArea.status === 'out')
    },
    // The location string this booking will actually be stored with, and so the
    // one the flag must be judged from. Written out once rather than at both
    // sites: submitBooking sending one string while bookingOutOfArea read
    // another would show a page that disagreed with what it wrote.
    bookingLocation () {
      return this.form.location || this.resolvedLocation()
    },
    // The province to name in the alert and the confirm. The SO path knows it
    // from the address it resolved; the appointment path reads it off the ตำบล
    // that was picked.
    bookingAreaProvince () {
      const loc = R.parseLocation(this.bookingLocation)
      if (loc && loc.province) return loc.province
      return (this.soArea && this.soArea.province) || ''
    },
    // Every province name the zonedv table knows, which is exactly the set
    // R.provinceOf can ever return from a stored address. The open-route dialog
    // picks from this rather than accepting free text: a typo there opened a
    // route no address would ever match, and nothing downstream could tell.
    // Empty until the dialog's watcher has loaded zonedv.json.
    //
    // The four provinces the small car already serves in full are left out: a
    // route to one is meaningless, and checkTripOpen refuses it as
    // PROVINCE_IN_AREA. Offering a choice the rule will reject is how a form
    // teaches people to distrust it. The partly-open provinces stay -- นครปฐม
    // and สมุทรสาคร are open at ตำบล level only, and a route there is exactly
    // the ตำบล the table leaves out.
    provinceOptions () {
      const seen = {}
      ;(this.zoneDv || []).forEach(e => {
        if (e.province && !isFullyServedProvince(e.province)) seen[e.province] = true
      })
      return Object.keys(seen).sort((a, b) => a.localeCompare(b))
    },
    // The same list, less what the route already reaches. Provinces another
    // open route holds that day stay offered: the board does not have the day
    // document in hand for every route date, and checkTripAddProvinces refuses
    // them by name, which says more than a province silently missing would.
    addProvinceOptions () {
      const have = this.addProvinceDialog.current
      return this.provinceOptions.filter(p => have.indexOf(p) === -1)
    },
    // Every province a route is going to inside the booking window, which is
    // what a shop is allowed to reach beyond the routine service area.
    // Read from `trips`, which subscribeTrips loads for the WHOLE booking
    // window, and not from byDate, which subscribeMonth scopes to the month on
    // screen. That scope was the bug: a shop looking at September was refused an
    // address whose route runs in October, and one that passed in September
    // found every October day greyed out.
    //
    // Only routes with a seat left. A route that is full or closed is not an
    // answer to "may this address be booked at all".
    provincesWithRoutes () {
      const out = []
      this.trips.forEach(t => {
        if (t.status !== R.TRIP_OPEN) return
        if ((t.count || 0) >= (t.capacity || 0)) return
        ;(t.provinces || []).forEach(p => { if (out.indexOf(p) === -1) out.push(p) })
      })
      return out
    },
    // Out of area, and nothing on this month's grid can take it. The chips grey
    // themselves out one by one and never say why between them; without this a
    // shop is left with a confirmed SO, a full calendar and no next step.
    noBookableDayNote () {
      if (!this.bookingOutOfArea || !this.days.length) return ''
      if (this.days.some(d => R.SLOTS.some(slot => this.canPick(d, slot)))) return ''
      return this.isStaff
        ? 'เดือนนี้ไม่มีวันที่เลือกได้ — เปลี่ยนเดือน หรือเปิดเส้นทางใหม่ในวันที่ต้องการ'
        : 'เดือนนี้ไม่มีวันที่มีเส้นทางไปพื้นที่นี้ — ลองเปลี่ยนเดือน หรือติดต่อเจ้าหน้าที่เพื่อขอเปิดเส้นทาง'
    },
    // The route this booking would sit on, on the day currently selected.
    bookingTripEntry () {
      if (!this.bookingOutOfArea || !this.selected.date) return null
      const day = this.byDate[this.selected.date] || {}
      return (day.tripByProvince || {})[this.bookingAreaProvince] || null
    },
    // Null for an in-area booking. bookingTripEntry is null there too, and
    // tripGateFor reads a null entry as "no route yet" -- which for staff is
    // 'openNew', so every in-area staff booking asked to open a route to
    // นนทบุรี and was refused as PROVINCE_IN_AREA.
    bookingTripGate () {
      if (!this.bookingOutOfArea) return null
      return R.tripGateFor(this.$store.state.status, this.bookingTripEntry)
    },
    bookingTripId () {
      const entry = this.bookingTripEntry
      return entry && R.tripStateOf(entry) === 'open' ? entry.tripId : null
    },
    // Clipped to the window a booking may actually land in rather than padded
    // out to the calendar month -- bookingMonthRange decides where the month
    // starts and where it stops. Sunday is the only exclusion left inside it,
    // and it stays drawn-but-disabled on purpose: that is a rule staff need to
    // see land on a real day, not a hole in the grid.
    days () {
      const range = R.bookingMonthRange(this.monthStart, this.earliest, this.latest)
      if (!range) return []
      const out = []
      const d = moment.tz(range.from, R.TZ)
      const end = moment.tz(range.to, R.TZ)
      while (d.isSameOrBefore(end)) {
        const date = d.format('YYYY-MM-DD')
        const doc = this.byDate[date] || null
        const counts = Object.assign({ am: 0, pm: 0, fullDay: 0, total: 0, outOfArea: 0 }, doc || {})
        const caps = S.capsForDay(doc, this.configCars, this.configPerCar)
        out.push({
          date,
          label: d.format('D MMM'),
          counts,
          caps,
          outOfAreaChips: this.dayOutOfAreaChips(date, doc),
          capNote: doc && doc.capNote ? doc.capNote : '',
          stopped: R.isDayStopped(doc),
          stopNote: doc && doc.stopNote ? doc.stopNote : '',
          over: counts.total > caps.total,
          // A Sunday delivery opened is an ordinary day; any other stays
          // drawn but disabled, with เปิดรับวันอาทิตย์ on it for staff.
          sunday: R.isSunday(date),
          sundayOpen: R.isSundayOpen(doc),
          sundayNote: doc && doc.sundayNote ? doc.sundayNote : '',
          disabled: R.isClosedSunday(date, doc)
        })
        d.add(1, 'day')
      }
      return out
    },
    // Out-of-area bookings with no route: made before routes existed (PR #35),
    // or left behind by a route that was deleted. Read off the same rows the
    // day list already has, so this costs no query.
    strayBookings () {
      return this.rows.filter(b => b.outOfArea && !b.tripId && R.isActive(b.status))
    }
  },
  methods: {
    upRef (b) { return U.orderRef(b) },
    // The result of a prefilled นัดหมาย, told back to the system that handed it
    // over (?appt=1&ref=&return=). Three ways, because none covers every case:
    //
    //   - postMessage to window.opener, when that system opened this tab. The
    //     date is already there before anyone clicks, so nothing needs to
    //     navigate at all and the salesperson keeps their place here. Sent to
    //     the return URL's own origin and NEVER '*' -- a message posted to any
    //     listener hands a customer's booking to whatever page happens to be
    //     there.
    //   - a countdown, when there is no opener (the link was opened by hand).
    //     Going back is what SHOULD happen, so it is what happens if nobody
    //     decides -- a yes/no question made "the shop never gets the date" the
    //     default, which is the opposite of the point.
    //   - a button that stays until the next booking, so cancelling the
    //     countdown keeps the way back instead of losing it.
    //
    // No countdown while a warning is on screen (a failed SAP write-back):
    // navigating away would take the warning with it. The button is still there.
    //
    // An address this app will not report to (config.apptReturnDomains) is said
    // out loud rather than silently skipped: the booking is fine, but somebody
    // has to know why the date did not travel back, and which domain to add.
    // The ref is on the booking either way, which is what links the two records.
    reportBookingBack (booked, sapNotes) {
      const ret = this.apptReturn
      if (!ret) return
      if (ret.rejected) {
        this.apptResult = { rejected: ret.rejected, host: '', url: '', posted: false, canFocus: false, seconds: 0 }
        return
      }
      const url = R.apptReturnUrl(ret, booked)
      if (!url) return
      let origin = ''
      try { origin = new URL(url).origin } catch (e) { return }
      let posted = false
      try {
        if (window.opener) {
          window.opener.postMessage({
            type: 'delivery:booked',
            bookedRef: ret.ref,
            date: booked.date,
            slot: booked.slot,
            bookingId: booked.bookingId
          }, origin)
          posted = true
        }
      } catch (e) {
        reportWarning('appointment handover: postMessage to the opener failed:', e)
      }
      this.apptResult = {
        rejected: '', host: ret.host, url: url, posted: posted, canFocus: posted, seconds: 0
      }
      // Only when nobody has been told yet and nothing needs reading.
      if (!posted && !sapNotes.length) this.startApptCountdown()
    },

    // Eight seconds, ticked down on screen with the host named and a ยกเลิก
    // beside it. A countdown someone can watch and stop is not a blind redirect:
    // the address already passed config.apptReturnDomains, and it is on screen.
    startApptCountdown () {
      this.stopApptCountdown()
      this.apptResult.seconds = APPT_RETURN_SECONDS
      this.apptTimer = setInterval(() => {
        if (!this.apptResult) return this.stopApptCountdown()
        this.apptResult.seconds -= 1
        if (this.apptResult.seconds <= 0) {
          this.stopApptCountdown()
          this.goBackToSource()
        }
      }, 1000)
    },

    stopApptCountdown () {
      if (this.apptTimer) clearInterval(this.apptTimer)
      this.apptTimer = null
      if (this.apptResult) this.apptResult.seconds = 0
    },

    goBackToSource () {
      const url = this.apptResult && this.apptResult.url
      this.stopApptCountdown()
      if (url) window.location.href = url
    },

    // The shop's own tab already has the date; this only brings it forward.
    focusSource () {
      try {
        if (window.opener) window.opener.focus()
      } catch (e) {
        reportWarning('appointment handover: focusing the opener failed:', e)
      }
    },

    // A new booking replaces the last one's handover -- the button belongs to
    // the booking it came from, and nothing else.
    clearApptResult () {
      this.stopApptCountdown()
      this.apptResult = null
    },

    resetJob () {
      this.steeredRt = null
      this.soData = null
      this.rtData = null
      this.soArea = null
      this.soProducts = []
      this.soNumber = ''
      this.rtNumber = ''
      this.orderDate = R.todayStr()
      this.itemCountFromSo = false
      // Every field the SO filled, not just the two from its address. The pin and
      // the landmark were listed by hand here and รายการสินค้า / จำนวนชิ้น /
      // เวลาติดตั้ง were added to the form later without being added here, so
      // เปลี่ยนเลขที่ SO carried the last SO's products into the next one -- and
      // applyProducts only fills รายการสินค้า when it is empty, so the stale
      // text survived the new lookup rather than being replaced by it.
      R.JOB_FIELDS.forEach(field => { this.form[field] = '' })
    },

    // The customer's details must not carry over into the next booking.
    // zonedv.json is ~1.5MB. carBooking.vue imports it eagerly; here it is
    // fetched only once someone actually types a zipcode, so the page itself
    // stays light and the chunk is shared with carBooking once either loads it.
    async ensureZoneDv () {
      if (!this.zoneDv) this.zoneDv = (await import('@/zonedv.json')).default
      return this.zoneDv
    },

    async loadSubDistricts (zip) {
      this.form.location = ''
      this.subDistrictOptions = []
      if (!/^\d{5}$/.test(zip || '')) return

      this.loadingZones = true
      try {
        const zoneDv = await this.ensureZoneDv()
        // Only the ตำบล the small car actually serves are offered, so an
        // out-of-area zipcode leaves nothing to pick and canSubmit -- which
        // requires form.location -- keeps จอง disabled even if the message below
        // is dismissed. Not filtered by zoneMain: that is routing data, and only
        // 1,195 of the 7,537 entries carry one.
        const rows = zoneDv.filter(e => String(e.zip) === String(zip))
        // Say why the ตำบล list is empty. Leaving it blank looks like the page
        // is still loading, and staff have no way to tell a typo apart from a
        // place we do not deliver to.
        if (!rows.length) {
          this.warn('ไม่พบรหัสไปรษณีย์', `ไม่พบรหัสไปรษณีย์ ${zip} โปรดตรวจสอบอีกครั้ง`)
          return
        }
        // Staff may open a route anywhere, so they see every ตำบล. A shop sees
        // the routine service area PLUS any province a route is going to inside
        // the booking window -- a route is a bounded, one-day extension of the
        // service area, and this is where a shop first meets it.
        const openProvinces = this.provincesWithRoutes
        const offered = this.isStaff
          ? rows
          : rows.filter(e => isServiceable(e) || openProvinces.indexOf(e.province) !== -1)
        if (!offered.length) {
          this.warn('อยู่นอกพื้นที่จัดส่ง',
            `รหัสไปรษณีย์ ${zip} (${rows[0].province}) อยู่นอกพื้นที่จัดส่ง — พื้นที่ที่จัดส่งได้ : ${areaLabel()}`)
          return
        }
        this.subDistrictOptions = offered.map(e => ({
          text: R.locationLabel(e) + (isServiceable(e) ? '' : ' — นอกพื้นที่'),
          value: R.formatLocation(e)
        }))
        // A zipcode covering exactly one ตำบล has nothing to choose, and a
        // handover that named one the list offers has already chosen it.
        if (this.subDistrictOptions.length === 1) this.form.location = this.subDistrictOptions[0].value
        else this.form.location = R.pickApptLocation(this.subDistrictOptions, this.apptLocation)
      } catch (e) {
        this.warn('ไม่สามารถโหลดข้อมูลตำบลได้', 'โปรดกรอกที่อยู่ให้ครบถ้วนแทน')
      } finally {
        this.loadingZones = false
      }
    },

    // Google Maps has no way to write back into our field, so the flow is
    // manual: open the map at the best place we can guess, let the user
    // right-click a point to copy its coordinates, then paste them here.
    openMapAt (query) {
      const url = query
        ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(query)
        : 'https://www.google.com/maps'
      window.open(url, '_blank', 'noopener')
    },

    // The edit dialog works from the stored booking, not the booking form.
    openMapForDialog () {
      const geo = R.parseGeo(this.dialog.fields.geo)
      const it = this.dialog.item || {}
      const loc = R.parseLocation(it.location)
      this.openMapAt(geo
        ? R.formatGeo(geo)
        : [it.address, loc && loc.subDt, loc && loc.district, loc && loc.province, it.zipcode]
          .filter(Boolean).join(' ').trim())
    },

    openMap () {
      const geo = R.parseGeo(this.form.geo)
      let query = ''
      if (geo) {
        query = R.formatGeo(geo)                 // already have a point -- open it
      } else {
        // No coordinate yet: search what the customer has typed so the map
        // opens near the delivery address instead of the middle of the world.
        const loc = R.parseLocation(this.form.location)
        query = this.fromSap
          // SO / RT path: the address comes from SAP and is read-only.
          ? ((this.soData && this.soData.custAddress) || '').trim()
          : [
            this.form.address,
            loc && loc.subDt, loc && loc.district, loc && loc.province,
            this.form.zipcode
          ].filter(Boolean).join(' ').trim()
      }
      this.openMapAt(query)
    },

    // Only when the address named exactly one ตำบล. A zipcode that straddles
    // several is left unresolved on purpose -- the same reason resolveFromAddress
    // answers 'unknown' rather than guessing which side of it an address is on.
    resolvedLocation () {
      const place = this.soArea && this.soArea.place
      if (place) return R.formatLocation(place)
      // No single ตำบล, but the address DID name a province. Storing '' here
      // threw that away: R.provinceOf then answered ไม่ระบุจังหวัด for a booking
      // sitting on a ราชบุรี route, so it could not be matched back to its own
      // route afterwards -- a move was refused as NO_TRIP for its owner and
      // silently stranded for staff.
      //
      // ",,ราชบุรี" is a location parseLocation reads as a province and nothing
      // else, which is exactly what is known. locationArea still answers 'out'
      // for it, so createBooking derives the flag rather than falling back to
      // the form's word for it.
      const province = (this.soArea && this.soArea.province) || ''
      return province ? `,,${province}` : ''
    },

    resetForm () {
      this.form = R.blankBookingForm()
      // A handover belongs to the form it arrived with, and to no other: one
      // link, one booking. Without this the next booking made in the same page
      // session would be stamped with the previous order's sourceRef and would
      // report ITS date back under that order's ref -- writing a delivery date
      // onto an order nobody booked. Cleared here because resetForm runs after
      // a successful booking (reportBookingBack has already read it by then)
      // and whenever the entry point changes.
      this.apptHandover = false
      this.apptReturn = null
      this.apptLocation = ''
      this.soArea = null
      this.soProducts = []
      this.subDistrictOptions = []
      if (this.$refs.jobForm) this.$refs.jobForm.resetValidation()
    },

    // Everything the page needs to know about an SO, in the order it has to be
    // asked. Both entry points go through here -- booking a new delivery against
    // an SO, and filling the number in on a นัดหมาย afterwards -- so the checks
    // a booking passes and the checks an attach passes cannot drift apart.
    //
    // Returns null when the SO is refused or staff decline a confirmation, having
    // already said why; throws only when the service itself failed, which the
    // caller reports.
    //
    //   excludeBookingId -- the booking being attached to. Without it the
    //                       duplicate check would find that same booking (once it
    //                       has the number) and refuse the retry.
    //   areaOut          -- 'refuse' on the booking form, where an out-of-area
    //                       address means don't book it. 'confirm' on an attach,
    //                       where the delivery is already on the calendar and a
    //                       customer is expecting it: refusing the number outright
    //                       would leave the job with no SO rather than move it out
    //                       of the area.
    //   withRt           -- also look up the return the SO carries (lookupSo).
    async fetchSo (soNo, { excludeBookingId, areaOut, withRt } = {}) {
      const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetSo`, { so: soNo })
      const rows = JSON.parse(res.data.d)
      if (!Array.isArray(rows) || rows.length === 0) {
        this.warn('ไม่พบข้อมูล', 'ไม่พบเลขที่ SO นี้ในระบบ')
        return null
      }
      const so = rows[0]
      if (so.canceled === 'Y') { this.warn('SO ถูกยกเลิก', 'เลขที่ SO นี้ถูกยกเลิกแล้ว'); return null }
      if (so.status === 'C') { this.warn('SO ถูกปิด', 'เลขที่ SO นี้ถูกปิดแล้ว'); return null }
      if (so.transNo) {
        this.warn('SO ถูกใช้งานแล้ว', `เลขที่ SO นี้ถูกใช้งานโดยใบจัดรถเลขที่ ${so.transNo}`)
        return null
      }

      // GetSo only knows about SOs already placed on a dispatch sheet. This flow
      // never sets transNo, so a second booking for the same SO would otherwise
      // pass every check above and eat a second seat.
      const existing = await S.findActiveBookingForSo(soNo)
      if (existing && existing.id !== excludeBookingId) {
        this.warn('SO นี้จองแล้ว', U.isUpcountry(existing)
          ? `เลขที่ SO นี้จองส่งรถใหญ่ต่างจังหวัดแล้ว${existing.date ? ' วันที่ ' + existing.date : ' (รอจัดรอบ)'}`
          : `เลขที่ SO นี้มีการจองวันที่ ${existing.date} ช่วง${R.SLOT_LABEL_TH[existing.slot]} อยู่แล้ว`)
        return null
      }

      const checked = await this.areaCheck(so, { areaOut, label: 'SO' })
      if (!checked) return null
      // Read before the return: fetchRtForSo's fallback needs the SO's original
      // sale, which only the product rows carry (saleSo).
      const products = await this.fetchProducts(soNo)
      if (checked.upcountry) {
        return Object.assign(checked, { products: products,
          docDate: so.docDate || '', orderDate: so.docDate || R.todayStr(),
          rt: withRt ? await this.fetchRtForSo(so, soNo, products) : null })
      }
      const area = checked.area

      return {
        so: so,
        area: area,
        // พิกัด and จุดสังเกต come from SAP's own fields when set, and otherwise
        // from a "จุดสังเกต" tail staff appended to the address before those
        // fields existed. Each shows in its own field and can be corrected, and
        // ที่อยู่ shows just the address.
        parsed: R.soAddressFields(so),
        products: products,
        // Raw, and empty when SAP did not send one. The booking form reads it
        // through orderDate below; an attach passes it straight through, because
        // "no docDate" and "today" are the same thing only for a booking being
        // made right now. See lookupSoForBooking.
        docDate: so.docDate || '',
        // docDate is not returned by GetSo today. The rule counts from it the
        // moment the ASMX side adds it; until then the lead time starts today.
        orderDate: so.docDate || R.todayStr(),
        // The SO's return, if it has one -- only when the caller asks (withRt):
        // ใส่ SO ignores it. Never refuses the SO: see fetchRtForSo.
        rt: withRt ? await this.fetchRtForSo(so, soNo, products) : null
      }
    },

    // The service-area half of fetchSo, moved out so the รับคืน tab can put an
    // RT row (GetRT has the same custAddress) through exactly the same gate.
    // Returns { so, area, parsed, upcountry? }, or null when the address is
    // refused or staff decline a confirmation, having already said why.
    //
    //   areaOut -- as fetchSo's.
    //   label   -- 'SO' or 'RT': which document the messages name.
    async areaCheck (so, { areaOut, label = 'SO' } = {}) {
      // The small car serves Bangkok and the provinces next to it. SAP hands us
      // the address as free text, so one that cannot be placed at all is put to
      // staff rather than refused -- a badly written address should not strand a
      // real order.
      let area = resolveFromAddress(so.custAddress, await this.ensureZoneDv(), undefined, { city: so.city })
      // The address named no province at all; the customer master did. Asked,
      // not assumed: that is the customer's province, which for a walk-in
      // account need not be where this delivery goes.
      if (area.source === 'city') {
        const useCity = await this.$root.$message(
          'ที่อยู่ไม่ระบุจังหวัด',
          [
            `ที่อยู่ของ ${label} นี้ไม่ระบุจังหวัด`,
            `ที่อยู่ : ${so.custAddress}`,
            `จังหวัดของลูกค้าใน SAP : ${area.province}`,
            '',
            `ใช้ จ.${area.province} เป็นจังหวัดจัดส่งหรือไม่? (ควรแก้ที่อยู่จัดส่งใน SAP ด้วย)`
          ],
          { type: 'confirm', color: 'warning', width: 450 }
        )
        if (!useCity) area = Object.assign({}, area, { status: 'unknown', province: '', source: '' })
      }
      if (area.status === 'out') {
        // Up-country (large truck) -- not for ใส่ SO (areaOut set), which attaches
        // to a small-car booking that already has a date.
        if (!areaOut && U.isRefused(area.province)) {
          this.warn('ไม่มีบริการจัดส่ง', `ไม่มีบริการจัดส่ง จ.${area.province}`)
          return null
        }
        if (!areaOut && U.groupsOf(area.province).length) {
          return { so, area, parsed: R.soAddressFields(so), upcountry: { province: area.province } }
        }
        const place = area.province === R.BANGKOK ? area.province : `จ.${area.province}`
        // A shop is refused; staff are told and decide. A caller may still ask
        // for 'confirm' outright -- the ใส่ SO path does, for a booking that
        // already has a date a customer is expecting -- so the role only
        // supplies the default.
        // A shop may reach an out-of-area address when a route is going there.
        // Which DATE is still decided by canPick against that day's route; this
        // only decides whether the address may be looked up at all, and the
        // ตำบล picker on the other entry point already works this way.
        const reachable = this.provincesWithRoutes.indexOf(area.province) !== -1
        if (!reachable && (areaOut || R.areaGateFor(this.$store.state.status)) !== 'confirm') {
          this.warn('อยู่นอกพื้นที่จัดส่ง',
            `ที่อยู่ของ ${label} นี้อยู่นอกพื้นที่จัดส่ง (${place}) — พื้นที่ที่จัดส่งได้ : ${areaLabel()}`)
          return null
        }
        const ok = await this.$root.$message(
          'อยู่นอกพื้นที่จัดส่ง',
          [
            `ที่อยู่ของ ${label} นี้อยู่นอกพื้นที่จัดส่ง (${place})`,
            `ที่อยู่ : ${so.custAddress}`,
            `พื้นที่ที่จัดส่งได้ : ${areaLabel()}`,
            '',
            // Three readers, three sentences. The attach path has a date
            // already. A shop may only take a route that exists. Staff may open
            // one, so telling them they are limited to existing routes would be
            // false -- the same split the alert above the day grid makes.
            areaOut === 'confirm'
              ? 'การจองนี้มีวันจัดส่งอยู่แล้ว ยืนยันที่จะใช้เลขที่ SO นี้หรือไม่?'
              : (this.isStaff
                ? 'เลือกวันที่มีเส้นทางไปพื้นที่นี้อยู่แล้ว หรือเลือกวันอื่นเพื่อเปิดเส้นทางใหม่ ซึ่งจะจองรถของวันนั้นไว้ 1 คัน'
                : 'การจองนอกพื้นที่จะจองได้เฉพาะวันที่มีเส้นทางไปพื้นที่นั้นอยู่แล้ว')
          ],
          { type: 'confirm', color: 'warning', width: 450 }
        )
        if (!ok) return null
      } else if (area.status === 'unknown') {
        const confirmed = await this.$root.$message(
          'ตรวจสอบพื้นที่จัดส่งไม่ได้',
          [
            `ไม่สามารถตรวจสอบจังหวัดจากที่อยู่ของ ${label} นี้ได้`,
            `ที่อยู่ : ${so.custAddress}`,
            `พื้นที่ที่จัดส่งได้ : ${areaLabel()}`,
            '',
            'ยืนยันว่าอยู่ในพื้นที่จัดส่งหรือไม่?'
          ],
          { type: 'confirm', color: 'warning', width: 450 }
        )
        if (!confirmed) return null
      }

      return { so, area, parsed: R.soAddressFields(so) }
    },

    // Same validation carBooking.vue applies, so staff see messages they know.
    async lookupSo () {
      // Trimmed here, once: every save after this reads soNumber, and a pasted
      // "226100028 " was stored as typed -- ค้นหา (which trims) could not find
      // it, and the already-booked check compared it against "226100028".
      this.soNumber = String(this.soNumber || '').trim()
      // A card from the last lookup must not sit under a refused one.
      this.rtData = null
      if (!this.soNumber) return
      this.loadingSo = true
      try {
        const found = await this.fetchSo(this.soNumber, { withRt: true })
        if (!found) return
        this.rtData = found.rt || null
        if (found.upcountry) {
          this.upFields = Object.assign(this.soFieldsFrom(found), {
            // null, not 0, when SAP sent no value: ฿0 is a real order
            // (service, exhibition stock) and only a missing one is refused.
            soNo: this.soNumber, orderValue: U.sapOrderValue(found.so.orderValue),
            orderDate: found.orderDate, user: this.$store.state.user
          }, found.rt && found.rt.fields ? found.rt.fields : {})
          this.upProvince = found.upcountry.province
          this.upDialog = true
          return
        }
        this.soArea = found.area
        this.soData = Object.assign({}, found.so, { custAddress: found.parsed.address })
        this.form.geo = found.parsed.geo
        this.form.landmark = found.parsed.landmark
        this.orderDate = found.orderDate
        this.applyProducts(found.products)
      } catch (e) {
        this.warn('เกิดข้อผิดพลาด', 'ไม่สามารถดึงข้อมูล SO หรือตรวจสอบการจองที่มีอยู่ได้ โปรดลองใหม่')
      } finally {
        this.loadingSo = false
      }
    },

    // รับคืน (มีเลขที่ RT). The RT's own checks, then the one question an RT
    // asks that an SO does not: does it belong to an SO that should carry it
    // instead (RJ.steerRt)? Only then the same area gate and up-country
    // handoff as fetchSo, through areaCheck with the RT row as `so`.
    async fetchRt (rtNo) {
      const found = await this.loadRt(rtNo)
      if (!found) { this.warn('ไม่พบข้อมูล', 'ไม่พบเลขที่ RT นี้ในระบบ'); return null }
      const problem = RJ.rtProblem(found.rt)
      if (problem) { this.warn('ใช้เลขที่ RT นี้ไม่ได้', RJ.rtProblemText(found.rt)); return null }
      const taken = await S.findActiveBookingForRt(rtNo)
      if (taken) {
        this.warn('RT นี้จองแล้ว', `เลขที่ RT นี้มีการจองวันที่ ${taken.date || '(รอจัดรอบ)'} อยู่แล้ว (${RJ.refText(taken)})`)
        return null
      }

      // Steering. The RT's own soNo is the ORIGINAL sale (closed, delivered
      // long ago), so it never steers -- the exchange's new SO is found from its
      // own side through GetSoByRt. GetSo is never asked for the number typed
      // here, which is an RT.
      let pick
      try {
        const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetSoByRt`, { rtNo: Number(rtNo) }, { timeout: 10000 })
        pick = RJ.pickExchangeSo(JSON.parse(res.data.d))
      } catch (e) {
        // Not knowing whether an open SO carries this RT is not "none does":
        // booking a pickup on that guess books the visit twice.
        reportWarning('GetSoByRt', e)
        this.warn('ตรวจสอบไม่ได้', 'ตรวจสอบ SO ของ RT นี้ไม่ได้ โปรดลองใหม่')
        return null
      }
      if (pick && pick.match === 'saleOrder') {
        // An open SO of the same original sale, not one that names this RT:
        // likely the exchange, but only a person can say.
        const orig = found.rt.soNo ? String(found.rt.soNo) : ''
        const yes = await this.$root.$message('เป็นการเปลี่ยนสินค้าหรือไม่',
          [orig ? `RT ${rtNo} มาจากใบขายเดิม SO ${orig}` : `RT ${rtNo}`,
            `พบ SO ${pick.soNo} ของใบขายเดิมนี้`]
            .concat(pick.others.length ? [`(และ SO อื่น: ${pick.others.join(', ')})`] : [])
            .concat([`เป็นการเปลี่ยนสินค้าของ SO ${pick.soNo} หรือไม่?`]),
          { type: 'confirm', color: 'info', width: 480 })
        if (!yes) pick = null
      }
      const soNo = pick ? pick.soNo : ''
      let so = null
      let soBooking = null
      if (soNo) {
        const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetSo`, { so: Number(soNo) })
        const rows = JSON.parse(res.data.d)
        so = Array.isArray(rows) && rows.length ? rows[0] : null
        soBooking = so ? await S.findActiveBookingForSo(soNo) : null
      }
      // attachCtx is what X.canAttachSo already reads: role, identities, user, profiles.
      const mayManage = !!soBooking && X.canAttachRt(soBooking, this.attachCtx)
      const steer = RJ.steerRt({ soNo, so, soBooking, mayManage, match: pick ? pick.match : '' })

      if (steer.action === 'BOOK_SO') {
        await this.$root.$message('เป็นการเปลี่ยนสินค้า',
          [`RT นี้เป็นการเปลี่ยนสินค้าของ SO ${steer.soNo}`, 'ระบบจะจองด้วยเลขที่ SO พร้อมรับคืนในรอบเดียวกัน'],
          { type: 'alert', color: 'info', width: 450 })
        this.mode = 'so'
        // The mode watcher clears the job on a switch; let it run first.
        await this.$nextTick()
        // After the watcher's resetJob, which clears it.
        this.steeredRt = { soNo: steer.soNo, rtNo: String(rtNo) }
        this.soNumber = steer.soNo
        await this.lookupSo()
        return null
      }
      if (steer.action === 'ATTACH') {
        await this.offerAttachRt(soBooking, found)
        return null
      }
      if (steer.action === 'WITH_SO') {
        this.warn('RT นี้ไปกับ SO แล้ว', `RT ${rtNo} ไปกับ SO ${steer.soNo} ในใบจัดรถเลขที่ ${steer.transNo} แล้ว — ไม่ต้องจองรับคืนแยก`)
        return null
      }
      if (steer.action === 'REFUSE_OWNED') {
        this.warn('SO นี้จองแล้ว',
          `RT นี้เป็นการเปลี่ยนสินค้าของ SO ${steer.soNo} ซึ่งจองไว้วันที่ ${soBooking.date || '(รอจัดรอบ)'} ` +
          `โดย ${this.$personName(soBooking.user)} — โปรดแจ้งผู้จองให้เพิ่ม RT ในการจองนั้น`)
        return null
      }

      // RETURN_ONLY: area and up-country exactly as an SO, by handing the RT row
      // to the same code. No products to deliver; the return lines ride in rt.
      const asSo = await this.areaCheck(found.rt, { label: 'RT' })
      if (!asSo) return null
      return Object.assign(asSo, {
        products: [],
        docDate: found.rt.docDate || '',
        orderDate: R.todayStr(),
        rt: Object.assign(found, { fields: RJ.rtFields(found.rt, found.rows, false), note: '' })
      })
    },

    async lookupRt () {
      this.rtNumber = String(this.rtNumber || '').trim()
      // A card from the last lookup must not sit under a refused one.
      this.rtData = null
      if (!this.rtNumber) return
      this.loadingSo = true
      try {
        const found = await this.fetchRt(this.rtNumber)
        if (!found) return
        this.rtData = found.rt
        if (found.upcountry) {
          this.upFields = Object.assign(this.soFieldsFrom(Object.assign({}, found, { products: [] })), {
            soNo: null, orderValue: 0, orderDate: found.orderDate, user: this.$store.state.user
          }, found.rt.fields)
          this.upProvince = found.upcountry.province
          this.upDialog = true
          return
        }
        this.soArea = found.area
        this.soData = Object.assign({}, found.so, { custAddress: found.parsed.address })
        this.form.geo = found.parsed.geo
        this.form.landmark = found.parsed.landmark
        this.orderDate = found.orderDate
        this.applyProducts([])
      } catch (e) {
        this.warn('เกิดข้อผิดพลาด', 'ไม่สามารถดึงข้อมูล RT หรือตรวจสอบการจองที่มีอยู่ได้ โปรดลองใหม่')
      } finally {
        this.loadingSo = false
      }
    },

    // The RT belongs to an SO the caller may manage, already booked: add the
    // return to that booking instead of booking the visit twice.
    async offerAttachRt (booking, found) {
      const lines = RJ.returnLines(found.rows).map(p => `• ${p.name} × ${p.qty}`)
      const ok = await this.$root.$message('เพิ่ม RT ในการจองนี้',
        [`RT ${found.rt.rtNo} เป็นการเปลี่ยนสินค้าของ SO ${booking.soNo}`,
          `ซึ่งจองไว้วันที่ ${booking.date || '(รอจัดรอบ)'}`, '', ...lines, '',
          'เพิ่มการรับคืนนี้ในการจองเดิมหรือไม่? (ไม่ใช้ที่ว่างเพิ่ม)'],
        { type: 'confirm', color: 'info', width: 480 })
      if (!ok) return
      try {
        const fields = RJ.rtFields(found.rt, found.rows, true)
        if (U.isUpcountry(booking)) {
          await US.attachRtUp(booking.id, { fields, user: this.$store.state.user, role: this.$store.state.status, identities: this.$store.getters.identities })
        } else {
          // The same caller context S.attachSo is given.
          await S.attachRt(booking.id, {
            fields,
            user: this.myEmail,
            owners: this.myIdentities,
            role: this.$store.state.status,
            profiles: this.$store.state.profiles
          })
        }
        // soNo '' writes the RT only -- the SO already has its date.
        const failed = await pushBookDateToSap(this.webPath,
          Object.assign({}, booking, { rtNo: String(found.rt.rtNo), soNo: '' }))
        const note = bookDateFailedNote(failed)
        await this.$root.$message('เพิ่ม RT แล้ว', [`การจอง SO ${booking.soNo} จะรับคืน RT ${found.rt.rtNo} ในรอบเดียวกัน`].concat(note ? [note] : []),
          { type: 'alert', color: note ? 'warning' : 'success', width: 450 })
        this.resetJob()
      } catch (e) {
        this.warn('เพิ่ม RT ไม่สำเร็จ', e.reason ? this.reasonText(e.reason) : 'โปรดลองใหม่')
      }
    },

    // The SO's product lines, through the same endpoint carBooking.vue uses.
    //
    // Never fatal, on either path: GetProdList being unavailable must not block
    // a booking or an SO number. The form falls back to reading รายการสินค้า, and
    // the planner falls back again to its default tier -- each step weaker, none
    // of them stopping the work. An empty array is what "it had nothing to say"
    // looks like to every caller, and mergeSoFields reads that as an absence
    // rather than as an instruction to blank what a person typed.
    async fetchProducts (soNo) {
      try {
        const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetProdList`,
          { so: soNo }, { timeout: 10000 })
        const rows = JSON.parse(res.data.d)
        // Field names confirmed against a real response: qty / prodCode /
        // prodName / front / frame / volume. They were guessed as quantity /
        // itemName / itemCode before anyone had seen one, which counted every
        // line as a single item however many that line was actually for.
        return Array.isArray(rows) ? rows : []
      } catch (e) {
        return []
      }
    },

    // GetRT + GetRtProdList for one RT number. null when SAP has no such RT.
    // Throws only when the service failed -- the caller decides whether that
    // stops anything (the SO path: never).
    async loadRt (rtNo) {
      const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetRT`, { rtNo: Number(rtNo) }, { timeout: 10000 })
      const rows = JSON.parse(res.data.d)
      if (!Array.isArray(rows) || !rows.length) return null
      const rt = rows[0]
      const prod = await axios.post(`${this.webPath}deliveryWebService.asmx/GetRtProdList`, { docEntry: rt.docEntry }, { timeout: 10000 })
      const items = JSON.parse(prod.data.d)
      return { rt, rows: Array.isArray(items) ? items : [] }
    },

    // The return an SO carries: its own U_KSK_ReturnNo, else the newest open RT
    // of the SO's ORIGINAL sale (GetRtBySo with the products' saleSo -- an RT
    // points at the original sale, never at the new SO). Never refuses the SO --
    // a return SAP cannot answer for is a note on the form, and คิวรถเล็ก's
    // confirm reads it again anyway. findActiveBookingForRt is the guard
    // against an RT the fallback finds that another booking already carries.
    async fetchRtForSo (so, soNo, products) {
      try {
        // The RT the รับคืน tab steered here with, for this SO only -- a
        // different SO typed afterwards gets no carried-over RT.
        const steered = this.steeredRt && this.steeredRt.soNo === String(soNo) ? this.steeredRt.rtNo : ''
        const known = RJ.knownRt({ steered, soRtNo: so.rtNo })
        let bySo = []
        const saleSo = known ? '' : RJ.saleSoOf(products)
        if (saleSo) {
          const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetRtBySo`, { so: Number(saleSo) }, { timeout: 10000 })
          bySo = JSON.parse(res.data.d)
        }
        const picked = RJ.pickRt(known ? known.rtNo : '', bySo)
        if (!picked) return null
        const found = await this.loadRt(picked.rtNo)
        if (!found) return { note: `ไม่พบเลขที่ RT ${picked.rtNo} ในระบบ — จองเป็นการส่งปกติ` }
        const problem = RJ.rtProblem(found.rt)
        if (problem) return { note: `RT ${picked.rtNo}: ${RJ.rtProblemText(found.rt)} — จองเป็นการส่งปกติ` }
        const taken = await S.findActiveBookingForRt(picked.rtNo)
        if (taken) return { note: `RT ${picked.rtNo} มีการจองวันที่ ${taken.date || '(รอจัดรอบ)'} อยู่แล้ว — จองเป็นการส่งปกติ` }
        const others = (known && known.conflict
          ? ` (ใช้ RT ${known.rtNo} ที่ระบุ — SO ${soNo} ใน SAP ระบุ RT ${known.conflict})` : '') +
          (picked.others.length
            ? ` (มี RT อื่นของ${saleSo ? 'ใบขายเดิม SO ' + saleSo : ' SO นี้'}: ${picked.others.join(', ')})` : '')
        return Object.assign(found, { fields: RJ.rtFields(found.rt, found.rows, true), note: others })
      } catch (e) {
        reportWarning('fetchRtForSo', e)
        return { note: 'ตรวจสอบ RT ไม่ได้ — จองเป็นการส่งปกติ ระบบจะตรวจอีกครั้งตอนยืนยันจัดรถ' }
      }
    },

    // The authoritative item count: how many lines the SO actually has, summing
    // quantity where a line reports one, because two of the same kiosk is two
    // installations rather than one line of work. Everything else -- the number
    // typed in the form, the one read out of free text -- is an estimate
    // standing in for this.
    applyProducts (rows) {
      this.soProducts = rows
      const total = I.itemsFromRows(rows)
      this.itemCountFromSo = total > 0
      if (total > 0) this.form.itemCount = String(total)
      // With the quantity in it. Names alone read as one item per line, so a
      // two-line SO for three kiosks seeded a รายการสินค้า that contradicted the
      // จำนวนชิ้น directly beneath it, and neither number said why.
      if (!this.form.productList && rows.length) {
        this.form.productList = I.productListText(rows)
      }
    },

    // One SO, shaped the way a booking document holds it. Read by the attach
    // dialog, and deliberately the same shape submitBooking writes, so a
    // นัดหมาย that gets its number later ends up indistinguishable from one
    // booked against the SO in the first place.
    soFieldsFrom ({ so, area, parsed, products }) {
      const total = I.itemsFromRows(products)
      const place = area && area.place
      return {
        name: so.custName || '',
        telephone: so.custTel || '',
        // parsed.address, not so.custAddress: the landmark and coordinate have
        // been lifted out of it into their own fields, and storing the raw line
        // as well would show the pin twice and let the two disagree.
        address: parsed.address || '',
        // The SO path has no ตำบล picker, but the address WAS resolved to one
        // when the service area was checked -- and only when it named exactly
        // one. The planner places a booking by pin, then location, then zipcode.
        location: place ? R.formatLocation(place) : '',
        zipcode: (area && area.zip) || '',
        geo: parsed.geo || '',
        landmark: parsed.landmark || '',
        productList: products.length ? I.productListText(products) : '',
        // null rather than 0 when nobody knows: installTime tells 'unknown' from
        // 'none', and mergeSoFields reads null as "the SO said nothing".
        itemCount: total > 0 ? total : null,
        prodList: products,
        // From the item master's per-piece volume × qty where GetProdList sends
        // it (I.volumeFromRows); the SO line's own volume only as a fallback.
        totalVolume: I.volumeFromRows(products),
        custName: so.custName || '',
        custTel: so.custTel || '',
        custAddress: parsed.address || '',
        city: so.city || '',
        cardCode: so.cardCode || '',
        cardName: so.cardName || ''
      }
    },

    // The row readers live in installTime.js, not here, so the total stored on
    // the booking, the breakdown drawn below, and the seeded รายการสินค้า text
    // cannot disagree about what one SAP line says. The older field spellings
    // stay as fallbacks there: this endpoint is shared with carBooking.vue and
    // nothing in this repo pins its response shape.
    productQty (row) { return I.productQty(row) },
    productLabel (row) { return I.productLabel(row) },
    productTotal (rows) { return I.itemsFromRows(rows) },

    // One booking row, read the way the planner reads it. These go through
    // installTime rather than through item.itemCount / item.installMinutes
    // directly, so what this list shows and what the day is planned on are the
    // same number -- including for a booking that has neither and falls back to
    // the free text or the default tier.
    bookingProducts (item) {
      return Array.isArray(item && item.prodList) ? item.prodList : []
    },

    // Only reached when there are no SAP rows: a นัดหมาย, or a booking made
    // before prodList was stored.
    productLines (item) {
      return String((item && item.productList) || '')
        .split(/[\n\r]+/).map(l => l.trim()).filter(Boolean)
    },

    bookingItems (item) { return I.itemsForBooking(item) },
    bookingInstall (item) { return I.minutesForBooking(item) },

    // Said out loud, because an unconfirmed count that looks identical to a
    // counted one is the reason someone opens this row in the first place.
    itemSourceNote (item) {
      const source = this.bookingItems(item).source
      if (source === 'default') return ' (ค่าเริ่มต้น)'
      if (source === 'productList') return ' (อ่านจากรายการ)'
      return ''
    },

    installSourceNote (item) {
      return this.bookingInstall(item).source === 'manual' ? ' (กำหนดเอง)' : ' (ค่าคำนวณ)'
    },

    // TOO_EARLY has to name the lead time actually in force, so no error copy is
    // read straight from the map.
    reasonText (reason) {
      return R.errorMessage(reason, { leadDays: this.configLeadDays, horizonDays: this.configHorizonDays })
    },

    warn (title, body) {
      this.$root.$message(title, body, { type: 'alert', color: 'error', width: 400 })
    },

    // Whole days between a past date and today, for the confirmation above.
    // Display only -- the rule that decides anything is the string comparison.
    daysAgo (dateStr) {
      return moment.tz(R.todayStr(), R.TZ).diff(moment.tz(dateStr, R.TZ), 'day')
    },

    shiftMonth (n) {
      this.monthStart = moment.tz(this.monthStart, R.TZ).add(n, 'month').format('YYYY-MM-01')
      this.watchMonth()
    },
    watchMonth () {
      if (this.unsubMonth) this.unsubMonth()
      const start = moment.tz(this.monthStart, R.TZ).startOf('month').format('YYYY-MM-DD')
      const end = moment.tz(this.monthStart, R.TZ).endOf('month').format('YYYY-MM-DD')
      this.unsubMonth = S.subscribeMonth(start, end,
        byDate => { this.byDate = byDate },
        // A dead listener would otherwise freeze the calendar on stale capacity
        // with no sign anything is wrong.
        () => this.warn('ไม่สามารถโหลดข้อมูลปฏิทินได้', 'การเชื่อมต่อขัดข้อง โปรดรีเฟรชหน้าจอ')
      )
    },
    // Straight off bookingDay.tripByProvince, which subscribeMonth already
    // loads. This is what makes the shop's calendar work with no extra query:
    // a shop that has typed a ราชบุรี address sees ราชบุรี 3/5 on the 15th and
    // nothing on other days.
    // ONE CHIP PER ROUTE, not per province. tripByProvince is keyed by province
    // because a lookup starts from an address, but a route covering two of them
    // appears under two keys with the same seats -- and drawn key by key, one
    // route to สมุทรสงคราม + สมุทรสาคร read as "0/5" twice. Ten seats and two
    // trucks, where there are five and one. R.tripsForDay does the grouping and
    // is asserted; this only turns it into text.
    dayOutOfAreaChips (date, doc) {
      return R.tripsForDay(doc).map(t => ({
        key: t.tripId,
        text: `${t.provinces.join(', ')} ${t.count}/${t.capacity}`,
        state: t.state
      // Day 2 of a route sent as 2 days: its car is still out, so the day has
      // one fewer (R.capacityForDay). Outlined and seatless -- the route's
      // bookings are on its departure day, and nobody books onto it here.
      })).concat(R.carriedRoutesOf(doc).map(r => ({
        key: 'carried-' + r.tripId,
        text: `${r.provinces.join(', ')} วันที่ ${r.day}/${r.days}`,
        title: `เส้นทางออก ${r.from}${r.regNum ? ' รถ ' + r.regNum : ''} ค้างคืน — รถ 1 คันไม่ว่างวันนี้`,
        state: 'carried'
      })))
    },

    // Routes replaced the loose out-of-area rows PR #36 listened for. The day
    // documents already carry every route's fill through tripByProvince, so the
    // calendar needs no listener of its own; this one is for the board, which
    // needs the trip documents themselves (history, who opened them, cars).
    watchTrips () {
      if (this.unsubTrips) this.unsubTrips()
      // Everyone, not just staff. The board is staff-only, but provincesWithRoutes
      // is what decides whether a SHOP may look up an out-of-area address at all,
      // and it needs the whole window rather than the month on screen.
      this.loadPlates()
      this.unsubTrips = S.subscribeTrips(
        R.todayStr(), this.latest,
        trips => { this.trips = trips },
        () => {}
      )
    },

    // A booking that took no seat on this day. It names its province, and greys
    // out when its route is closed, so a row in the day list says the same thing
    // the day card above it says.
    tripChip (item) {
      const day = this.byDate[item.date] || {}
      const entry = (day.tripByProvince || {})[R.provinceOf(item)] || null
      const province = R.provinceOf(item)
      return {
        text: province === R.NO_PROVINCE ? 'นอกพื้นที่' : province,
        color: R.tripStateOf(entry) === 'open' ? 'deep-purple' : 'grey'
      }
    },

    async doOpenTrip () {
      this.openTripDialog.saving = true
      try {
        await S.openTrip({
          date: this.openTripDialog.date,
          provinces: this.openTripDialog.provinces,
          capacity: this.openTripDialog.capacity,
          cars: this.openTripDialog.cars,
          user: this.myEmail
        })
        this.openTripDialog.open = false
        this.$root.$message('เปิดเส้นทางแล้ว',
          `เปิดเส้นทาง ${this.openTripDialog.provinces.join(', ')} วันที่ ${this.openTripDialog.date} แล้ว`,
          { type: 'alert', color: 'success', width: 420 })
      } catch (e) {
        this.warn('เปิดเส้นทางไม่สำเร็จ',
          e.reason ? this.reasonText(e.reason) : 'เกิดข้อผิดพลาด โปรดลองใหม่')
      } finally {
        this.openTripDialog.saving = false
      }
    },
    openStopDialog (d) {
      this.stopDialog = {
        open: true,
        date: d.date,
        label: d.label,
        stopped: d.stopped,
        note: d.stopNote || '',
        noteRequired: !d.stopped && R.stopNoteRequired(d.date),
        counts: d.counts,
        caps: d.caps,
        saving: false
      }
    },
    async saveStopDialog () {
      const sd = this.stopDialog
      sd.saving = true
      try {
        await S.setDayStopped(sd.date, { stopped: !sd.stopped, note: sd.note, user: this.myEmail })
        sd.open = false
        this.$root.$message(sd.stopped ? 'เปิดรับจองแล้ว' : 'ปิดรับจองแล้ว',
          sd.stopped
            ? `วันที่ ${sd.date} เปิดให้ร้านค้าจองได้อีกครั้ง`
            : `วันที่ ${sd.date} ร้านค้าจะจองหรือย้ายการจองมาวันนี้ไม่ได้ จนกว่าจะเปิดรับจองอีกครั้ง`,
          { type: 'alert', color: 'success', width: 420 })
      } catch (e) {
        this.warn(sd.stopped ? 'เปิดรับจองไม่สำเร็จ' : 'ปิดรับจองไม่สำเร็จ', e.message || 'โปรดลองใหม่อีกครั้ง')
      } finally {
        sd.saving = false
      }
    },
    openSundayDialog (d) {
      this.sundayDialog = { open: true, date: d.date, label: d.label, cars: this.configCars, note: '', saving: false }
    },
    async saveSundayDialog () {
      const sd = this.sundayDialog
      sd.saving = true
      try {
        await S.setSundayOpen(sd.date, { cars: sd.cars, note: sd.note, user: this.myEmail })
        sd.open = false
        this.$root.$message('เปิดรับจองวันอาทิตย์แล้ว',
          `วันที่ ${sd.date} ร้านค้าและเจ้าหน้าที่จองได้ ${sd.cars} คัน`,
          { type: 'alert', color: 'success', width: 420 })
      } catch (e) {
        this.warn('เปิดรับจองวันอาทิตย์ไม่สำเร็จ', e.reason ? this.reasonText(e.reason) : (e.message || 'โปรดลองใหม่อีกครั้ง'))
      } finally {
        sd.saving = false
      }
    },
    async doCloseTrip (tripId) {
      await this.tripAction(() => S.closeTrip(tripId, { user: this.myEmail }), 'ปิดรับเส้นทางแล้ว')
    },
    async doReopenTrip (tripId) {
      await this.tripAction(() => S.reopenTrip(tripId, { user: this.myEmail }), 'เปิดรับเส้นทางอีกครั้งแล้ว')
    },
    // The undo for a route opened on the wrong date, and the only thing that
    // gives its truck back to the day. Refused by the transaction if a booking
    // has landed on it since the board was drawn.
    // Small then large, which is the order fleet.js already returns them in and
    // the order dispatch thinks about them in. Failure is quiet and leaves the
    // list empty: the dialog then has nothing to pick and says so by disabling
    // its button, which is better than an alert over a board nobody may be using.
    async loadPlates () {
      if (this.plates.length) return
      try {
        const fleet = await F.loadFleet()
        // Small plates only: คิวรถเล็ก draws only these, so a job sent on a large plate was invisible there.
        this.plates = (fleet.small || [])

      } catch (e) {
        this.plates = []
      }
    },

    // The step that closes the hole trips opened: dispatchService writes a
    // `booking` document per job on the route, carrying พิกัด and จุดสังเกต,
    // so nothing has to be retyped into คิวรถเล็ก by hand.
    async doSendTrip ({ tripId, regNum, days }) {
      try {
        const r = await D.sendTripToDispatch(tripId, { regNum: regNum, user: this.myEmail, days: days })
        // Both numbers, because "sent 0, skipped 3" is the answer to "why did
        // nothing happen" and a bare success would not give it.
        //
        // คิวรถเล็ก and not จัดรถ: this wrote `booking` documents, and จัดรถ
        // edits `deliveryList` rows, which do not exist yet. carBooking is the
        // single gate between the two -- its confirm step is what creates the
        // deliveryList / deliveryTimeline pair. Naming จัดรถ here sent staff to
        // a page that cannot see what they had just created.
        const skipped = r.skipped ? ` (มีอยู่ในคิวแล้ว ${r.skipped} งาน)` : ''
        this.$root.$message(
          r.created ? 'ส่งเข้าคิวจัดรถแล้ว' : 'ไม่มีงานใหม่ที่ต้องส่ง',
          [r.created
            ? `ส่ง ${r.created} งานเข้าคิวของรถ ${r.regNum} แล้ว${skipped} — ยืนยันการจัดรถและจัดลำดับการส่งได้ที่ คิวรถเล็ก`
            : `งานของเส้นทางนี้อยู่ในคิวจัดรถแล้วทั้งหมด${skipped}`].concat(R.overDayLines(r.overDay)),
          { type: 'alert', color: r.overDay ? 'warning' : (r.created ? 'success' : 'info'), width: 480 })
      } catch (e) {
        const why = {
          NO_CAR: 'ยังไม่ได้เลือกรถ',
          NO_TRIP: 'ไม่พบเส้นทางนี้',
          TRIP_CANCELLED: 'เส้นทางนี้ถูกยกเลิกแล้ว',
          TRIP_EMPTY: 'เส้นทางนี้ยังไม่มีการจอง'
        }[e && e.message]
        this.warn('ส่งเข้าคิวจัดรถไม่สำเร็จ', why || 'เกิดข้อผิดพลาด โปรดลองใหม่')
      }
    },

    async doTripDays ({ tripId, days }) {
      try {
        const r = await D.setTripDays(tripId, { days, user: this.myEmail })
        this.$root.$message('แก้จำนวนวันแล้ว', [`${days} วัน · งานในคิว ${r.jobs} · งานที่จัดรถแล้ว ${r.rows}`].concat(R.overDayLines(r.overDay)),
          { type: 'alert', color: r.overDay ? 'warning' : 'success' })
      } catch (e) {
        reportError('setTripDays', e)
        const why = { BAD_DAYS: 'จำนวนวันไม่ถูกต้อง', NO_TRIP: 'ไม่พบเส้นทางนี้', NOT_SENT: 'เส้นทางนี้ยังไม่ได้ส่งเข้าคิวจัดรถ' }[e && e.message]
        this.$root.$message('แก้จำนวนวันไม่สำเร็จ', why || String((e && e.message) || e), { type: 'alert', color: 'error' })
      }
    },

    async doCancelTrip (tripId) {
      await this.tripAction(() => S.cancelTrip(tripId, { user: this.myEmail }), 'ยกเลิกเส้นทางแล้ว')
    },
    async doSetTripCapacity ({ tripId, capacity }) {
      await this.tripAction(() => S.setTripCapacity(tripId, { capacity, user: this.myEmail }), 'บันทึกจำนวนงานแล้ว')
    },
    openAddProvince (t) {
      this.addProvinceDialog = {
        open: true,
        tripId: t.id,
        date: t.date,
        current: (t.provinces || []).slice(),
        count: t.count || 0,
        capacity: t.capacity || 0,
        provinces: [],
        saving: false
      }
      // zonedv.json is loaded on demand; nothing may have asked for it yet.
      this.ensureZoneDv()
    },
    async doAddProvince () {
      const d = this.addProvinceDialog
      d.saving = true
      try {
        const added = await S.addTripProvinces(d.tripId, { provinces: d.provinces, user: this.myEmail })
        d.open = false
        this.$root.$message('เพิ่มจังหวัดแล้ว',
          `เส้นทางวันที่ ${d.date} ไปถึง ${added.join(', ')} ด้วยแล้ว`,
          { type: 'alert', color: 'success', width: 420 })
      } catch (e) {
        this.warn('เพิ่มจังหวัดไม่สำเร็จ',
          e.reason ? this.reasonText(e.reason) : 'เกิดข้อผิดพลาด โปรดลองใหม่')
      } finally {
        d.saving = false
      }
    },
    // Confirmed first: a chip's ✕ is small and sits next to the province it is
    // not meant for. The transaction refuses a province anyone is booked in, and
    // says how many, so the operator knows what to move.
    async doRemoveProvince ({ trip, province }) {
      const ok = await this.$root.$message(
        'นำจังหวัดออกจากเส้นทาง',
        `นำ ${province} ออกจากเส้นทางวันที่ ${trip.date} หรือไม่? ร้านค้าจะจองจังหวัดนี้ในเส้นทางนี้ไม่ได้อีก`,
        { type: 'confirm', color: 'warning', width: 420 })
      if (!ok) return
      try {
        await S.removeTripProvince(trip.id, { province, user: this.myEmail })
        this.$root.$message('สำเร็จ', `นำ ${province} ออกจากเส้นทางแล้ว`,
          { type: 'alert', color: 'success', width: 380 })
      } catch (e) {
        const many = e.reason === 'PROVINCE_HAS_BOOKINGS' && e.count ? ` (${e.count} รายการ)` : ''
        this.warn('นำจังหวัดออกไม่สำเร็จ',
          (e.reason ? this.reasonText(e.reason) : 'เกิดข้อผิดพลาด โปรดลองใหม่') + many)
      }
    },
    async doAttachToTrip ({ bookingId, tripId }) {
      await this.tripAction(() => S.attachToTrip(bookingId, tripId, { user: this.myEmail }), 'เพิ่มเข้าเส้นทางแล้ว')
    },
    // One reporting path for four one-line writes. Each of them can be refused
    // by the transaction -- a route that filled while the board was open is the
    // ordinary case, not the exception -- so none of them may fail silently.
    async tripAction (run, okText) {
      try {
        await run()
        this.$root.$message('สำเร็จ', okText, { type: 'alert', color: 'success', width: 380 })
      } catch (e) {
        this.warn('ไม่สำเร็จ', e.reason ? this.reasonText(e.reason) : 'เกิดข้อผิดพลาด โปรดลองใหม่')
      }
    },
    // Point the table at the day the route runs on, and let the existing day
    // view do the rest. Deliberately not a fourth rowsMode with a filter of its
    // own: a route's bookings are all on one date, its own chips already mark
    // them, and the day view is what staff use to act on a booking anyway. One
    // less piece of list state that can disagree with the table.
    pickTripRows (tripId) {
      const trip = this.trips.find(t => t.id === tripId)
      if (!trip) return
      this.search.date = trip.date
      this.rangeMode = 'day'
      // Assigning the same date the field already holds fires no watcher, so
      // the list is re-subscribed explicitly -- the same reason clearSearch
      // calls showList by hand.
      this.showList()
      // The list now sits above the board, often off screen, so take the
      // person to what they asked to see.
      this.scrollToList()
    },

    // Smooth for a click. Instant on arrival from a link: Chrome does not run
    // a smooth scroll in a background tab, which is how a link opened in a new
    // tab often starts, and the page was found still at the top.
    scrollToList ({ smooth = true } = {}) {
      const list = this.$refs.bookingList
      if (list && list.$el) list.$el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' })
    },

    canPick (d, slot) {
      if (d.disabled) return false
      // The same rule createBooking enforces, so a shop is never offered a chip
      // the transaction would refuse. Staff pass it and are asked on submit.
      if (!R.checkDayOpen(this.byDate[d.date], this.$store.state.status, { outOfArea: this.bookingOutOfArea }).ok) {
        return false
      }
      if (this.bookingOutOfArea) {
        // A route's seats, not the day's. Staff may pick a day with no route --
        // booking there opens one. A shop may pick only a day whose route is
        // open with room, which is exactly the offer the day card shows.
        const entry = ((this.byDate[d.date] || {}).tripByProvince || {})[this.bookingAreaProvince] || null
        return ['book', 'openNew'].indexOf(R.tripGateFor(this.$store.state.status, entry)) !== -1
      }
      return R.checkCapacity(d.counts, d.caps, slot).ok
    },
    remaining (d, slot) {
      if (slot === 'full') return d.caps.total - d.counts.total
      return Math.min(d.caps[slot] - d.counts[slot], d.caps.total - d.counts.total)
    },
    chipText (d, slot) {
      if (d.disabled) return ''
      // Seat counts describe a resource this booking does not draw on, and
      // 'เต็ม' on a chip that is clickable reads as a bug.
      if (this.bookingOutOfArea) return ''
      // Said on the chip itself for a shop, which cannot pick it. Staff can, so
      // they keep the seat counts they need to decide whether to.
      if (d.stopped && !this.isStaff) return 'ปิดรับ'
      const left = this.remaining(d, slot)
      if (left <= 0) return 'เต็ม'
      // ทั้งวัน has no cap of its own -- it draws on the day total beside the
      // date -- so it shows a count with no denominator. Without it the chips
      // added up to less than that total and the difference looked lost.
      return slot === 'full' ? String(d.counts.fullDay || 0) : `${d.counts[slot]}/${d.caps[slot]}`
    },
    chipColor (d, slot) {
      if (d.disabled || !this.canPick(d, slot)) return 'grey lighten-1'
      if (this.selected.date === d.date && this.selected.slot === slot) return 'orange accent-3'
      // "Nearly full" is not a thing that can be said about a day to a booking
      // that takes no seat on it.
      if (this.bookingOutOfArea) return 'green'
      return this.remaining(d, slot) <= 2 ? 'amber darken-2' : 'green'
    },
    chipDark (d, slot) { return this.canPick(d, slot) },
    pick (date, slot) { this.selected = { date, slot } },

    async submitBooking () {
      // The previous booking's way back belongs to that booking, not this one.
      this.clearApptResult()
      if (this.mode === 'appointment' && this.$refs.jobForm && !this.$refs.jobForm.validate()) {
        return this.warn('ข้อมูลไม่ครบถ้วน', 'โปรดตรวจสอบข้อมูลลูกค้าให้ถูกต้องครบถ้วน')
      }
      if (this.form.geo && !R.parseGeo(this.form.geo)) {
        return this.warn('พิกัดไม่ถูกต้อง', 'รูปแบบพิกัดไม่ถูกต้อง เช่น 13.7563, 100.5018')
      }
      // installTime clamps out-of-range durations rather than trusting them, so
      // saving one would silently plan a different day than the form showed.
      // Refuse it here where staff can still see what they typed.
      if (this.installOutOfRange) {
        return this.warn('เวลาติดตั้งไม่ถูกต้อง',
          `เวลาติดตั้งต้องอยู่ระหว่าง ${I.MIN_INSTALL_MINUTES} ถึง ${I.MAX_INSTALL_MINUTES} นาที`)
      }
      // A นัดหมาย has no SO, and an up-country order cannot be booked without one.
      if (this.mode === 'appointment' && U.groupsOf(R.provinceOf({ location: this.bookingLocation })).length) {
        return this.warn('ต้องมีเลขที่ SO', R.errorMessage('UP_NO_SO'))
      }
      // The SO path was already asked, inside fetchSo, and asking twice for one
      // booking is how people learn to click through both. The appointment path
      // has had nothing but a labelled ตำบล, and this is a promise to send a
      // truck to another province, so it is put in words once.
      if (this.mode === 'appointment' && this.bookingOutOfArea) {
        const province = this.bookingAreaProvince
        const place = province === R.BANGKOK ? province : `จ.${province}`
        const opening = this.bookingTripGate === 'openNew'
        const ok = await this.$root.$message(
          opening ? 'เปิดเส้นทางนอกพื้นที่' : 'จองเข้าเส้นทางนอกพื้นที่',
          opening
            ? [
              `ที่อยู่นี้อยู่นอกพื้นที่จัดส่งปกติ (${place})`,
              `วันที่ ${this.selected.date} ยังไม่มีเส้นทางไปพื้นที่นี้`,
              '',
              `การจองนี้จะเปิดเส้นทางใหม่ และจองรถ 1 คันของวันนั้นไว้ (รับได้ ${R.normalizePerCar(this.configPerCar).total} งาน)`,
              'ยืนยันที่จะเปิดเส้นทางหรือไม่?'
            ]
            : [
              `จองเข้าเส้นทาง ${place} วันที่ ${this.selected.date}`,
              `เหลือที่ว่าง ${R.tripSeatsLeft(this.bookingTripEntry)} งาน`,
              '',
              'ยืนยันที่จะจองหรือไม่?'
            ],
          { type: 'confirm', color: 'warning', width: 450 }
        )
        if (!ok) return
      }
      // Staff may book past ปิดรับจอง -- delivery keeps the last word -- but the
      // person who stopped the day may not be the person booking into it, so the
      // reason is put in front of them before they add to it.
      if (this.isStaff && !this.bookingOutOfArea && R.isDayStopped(this.byDate[this.selected.date])) {
        const note = (this.byDate[this.selected.date] || {}).stopNote
        const ok = await this.$root.$message(
          'วันนี้ปิดรับจองแล้ว',
          [
            `วันที่ ${this.selected.date} ถูกปิดรับจองไว้${note ? ` (${note})` : ''}`,
            'ร้านค้าจะจองวันนี้ไม่ได้ แต่เจ้าหน้าที่ยังจองได้',
            '',
            'ยืนยันที่จะจองวันนี้หรือไม่?'
          ],
          { type: 'confirm', color: 'warning', width: 450 }
        )
        if (!ok) return
      }
      // The same customer already booked this day and slot: one stop, one seat,
      // if a person says so. Asked, never assumed -- a shared switchboard number
      // can make two strangers look alike. A failed lookup only skips the ask.
      let visitWith = null
      {
        const so0 = this.soData || {}
        let found = []
        try {
          found = await S.findVisitCandidates({
            date: this.selected.date,
            slot: this.selected.slot,
            telephone: this.form.telephone || so0.custTel || '',
            location: this.bookingLocation,
            outOfArea: this.bookingOutOfArea,
            tripId: this.bookingTripId
          })
        } catch (e) {
          reportWarning('findVisitCandidates', e)
        }
        if (found.length) {
          const c = found[0]
          const join = await this.$root.$message('ลูกค้ารายนี้มีการจองวันนี้แล้ว', [
            `${c.name || ''} — ${RJ.refText(c)} วันที่ ${this.selected.date} ช่วง${R.SLOT_LABEL_TH[this.selected.slot]}`,
            '',
            'รวมเป็นจุดส่งเดียวกันหรือไม่? (ส่งพร้อมกัน ใช้ที่ว่างของวันเพียง 1 งาน)'
          ], { type: 'confirm', color: 'teal', width: 480 })
          if (join) visitWith = c.id
        }
      }
      this.submitting = true
      try {
        const so = this.soData || {}
        const typed = Math.floor(Number(this.form.itemCount))
        const itemCount = isFinite(typed) && typed > 0
          ? typed
          : (I.countFromProductList(this.form.productList).items || null)
        const bookingId = await S.createBooking({
          soNo: this.mode === 'so' ? this.soNumber : null,
          date: this.selected.date,
          slot: this.selected.slot,
          name: this.form.name || so.custName || '',
          telephone: this.form.telephone || so.custTel || '',
          address: this.form.address || so.custAddress || '',
          // The SO path has no ตำบล picker, but the address WAS resolved to one
          // when the service area was checked. Storing it was the missing step:
          // without it a booking went to Firestore with an empty `location`, and
          // the planner -- which places a booking by pin, then location, then
          // zipcode -- had nothing to work with and refused ordinary deliveries
          // to ต.พิมลราช and ต.คลองสอง as "ระบุตำแหน่งไม่ได้". It also leaves the
          // zone lookup with nothing to key on.
          location: this.bookingLocation,
          // Read only where `location` resolved to nothing for createBooking to
          // judge -- an SO whose address named no single ตำบล. Everywhere else
          // it re-derives from the location itself and this is ignored, which is
          // what stops a stale form storing a flag its address contradicts.
          outOfArea: this.bookingOutOfArea,
          tripId: this.bookingTripId,
          // No route yet: this booking opens one and takes its first seat, in
          // createBooking's single transaction. Two calls could half-fail and
          // strand a reserved truck on a day with nobody on it.
          openTrip: this.bookingTripGate === 'openNew' && this.bookingAreaProvince
            ? { provinces: [this.bookingAreaProvince],
                capacity: R.normalizePerCar(this.configPerCar).total,
                cars: 1 }
            : null,
          zipcode: this.form.zipcode || (this.soArea && this.soArea.zip) || '',
          productList: this.form.productList || '',
          // Carried so the appointment can show what is being delivered. Volume
          // is summed the way carBooking sums it for a dispatched round, so the
          // two agree rather than each inventing a total.
          prodList: this.soProducts,
          totalVolume: I.volumeFromRows(this.soProducts),
          // The planner builds its whole timetable on this. Stored as a number
          // so it never has to be re-parsed, and omitted rather than stored as
          // 0 when nobody said -- installTime tells 'unknown' from 'none'.
          itemCount: itemCount,
          // And this overrules it. Null, not 0, for the same reason: a stop of
          // no minutes and a stop nobody has timed are different facts, and only
          // one of them should stop the tiers from applying.
          installMinutes: this.installOverride,
          geo: R.formatGeo(R.parseGeo(this.form.geo)),
          landmark: this.form.landmark || '',
          custName: so.custName || '',
          custTel: so.custTel || '',
          custAddress: so.custAddress || '',
          city: so.city || '',
          cardCode: so.cardCode || '',
          cardName: so.cardName || '',
          // An exchange: the SO's return, found on lookup (RJ.rtFields). A
          // return-only booking (the รับคืน tab) sets these the same way.
          ...((this.rtData && this.rtData.fields) || {}),
          // The other system's own order number, when this booking was handed
          // over prefilled (?appt=1&ref=). Stored so the two records stay linked
          // whether or not the result ever makes it back -- without it the only
          // thing joining them is the customer's phone.
          sourceRef: (this.apptReturn && this.apptReturn.ref) || '',
          visitWith: visitWith,
          user: this.$store.state.user || ''
        }, this.orderDate, { role: this.$store.state.status })

        // A พิกัด / จุดสังเกต changed from what the SO held in SAP goes back to
        // it -- including one lifted from the address's จุดสังเกต tail into a
        // field SAP had empty. After the booking is saved, never instead of it.
        // And the booked date itself, so SAP shows this delivery before จัดรถ
        // does. A นัดหมาย writes nothing -- it has no SO yet, and ใส่ SO is
        // where it reaches SAP. Both writes touch different fields and neither
        // depends on the other, so the customer waits for one, not two.
        const [failed, dateFailed] = await Promise.all([
          // A return-only booking's pin goes to its RT (ORDN), an SO's to the SO.
          this.fromSap
            ? pushBookingPinToSap(this.webPath,
              { soNo: this.mode === 'so' ? this.soNumber : null, rtNo: (this.rtData && this.rtData.fields && this.rtData.fields.rtNo) || '',
                jobType: this.mode === 'rt' ? 'return' : 'delivery' },
              R.sapPinPatch(R.soPin(so), { geo: this.form.geo, landmark: this.form.landmark }))
            : [],
          pushBookDateToSap(this.webPath,
            { soNo: this.mode === 'so' ? this.soNumber : '',
              rtNo: (this.rtData && this.rtData.fields && this.rtData.fields.rtNo) || '',
              date: this.selected.date, status: 'booked' })
        ])
        const sapNotes = [sapFailedNote(failed), bookDateFailedNote(dateFailed)].filter(Boolean)
        const booked = { date: this.selected.date, slot: this.selected.slot, bookingId: bookingId }
        await this.$root.$message('จองสำเร็จ',
          [`จองวันที่ ${this.selected.date} ช่วง${R.SLOT_LABEL_TH[this.selected.slot]} เรียบร้อยแล้ว`]
            .concat(sapNotes),
          { type: 'alert', color: sapNotes.length ? 'warning' : 'success', width: sapNotes.length ? 450 : 400 })
        // A booking handed over from another system reports back to it, so the
        // salesperson does not retype a date they just chose. After the alert,
        // so a countdown never runs behind a message nobody has read yet.
        this.reportBookingBack(booked, sapNotes)
        this.selected = { date: '', slot: '' }
        this.resetJob()
        this.resetForm()
      } catch (e) {
        // The transaction rejects when the slot filled between render and submit.
        this.warn('ไม่สามารถจองได้', e.reason ? this.reasonText(e.reason) : 'เกิดข้อผิดพลาด โปรดลองใหม่')
      } finally {
        this.submitting = false
      }
    },

    async init () {
      // Config is a convenience: DEFAULT_CARS already covers its absence, so a
      // failure here must not stop the calendar from subscribing.
      try {
        const cfg = await S.getConfig()
        this.configCars = cfg.cars
        this.defaultCars = cfg.cars
        this.capForm.cars = cfg.cars
        this.configPerCar = Object.assign({}, cfg.perCar)
        this.defaultPerCar = Object.assign({}, cfg.perCar)
        this.configLeadDays = cfg.leadDays
        this.defaultLeadDays = cfg.leadDays
        this.configHorizonDays = cfg.horizonDays
        this.defaultHorizonDays = cfg.horizonDays
      } catch (e) {
        this.warn('ไม่สามารถโหลดค่าจำนวนรถได้', 'ใช้ค่ามาตรฐานแทน')
      }
      this.watchMonth()
      // After getConfig, not before: `latest` is horizonDays out from today, and
      // subscribing first would watch the default window and never widen it.
      this.watchTrips()
      this.watchUpOrders()
      this.showList()
      this.applyRouteQuery()
    },

    // Replaced the รอยืนยัน queue when the confirm step was removed. Everyone
    // gets this list; a non-staff login is scoped to its own bookings by the
    // owners argument rather than by hiding rows in the template.
    //
    // Re-subscribed rather than filtered client-side whenever the day or the
    // mode changes: the query is one range filter on `date`, so a narrow view
    // downloads only the day it shows. Staying live is the point -- a booking
    // moved onto or off the day being dispatched appears while it is on screen.
    showList () {
      if (this.unsubList) this.unsubList()
      this.rowsMode = 'list'
      // Choosing a day answers a different question than a phone number does,
      // and the list can only answer one at a time. Leaving the term in the box
      // while the list showed a whole day read as though the day were filtered
      // by it.
      this.search.tel = ''
      this.search.soNo = ''
      this.expanded = []
      this.listLoading = true
      this.unsubList = S.subscribeRange(
        this.listFrom, this.listTo, this.canSeeAll ? null : this.myIdentities,
        rows => {
          this.listLoading = false
          // A search result must not be overwritten by a live snapshot arriving
          // behind it -- the rows still stream in, they just wait their turn.
          if (this.rowsMode === 'list') this.rows = rows
        },
        () => {
          // Cleared on the error path too, or a dropped connection would leave
          // the table spinning as though the day were still loading.
          this.listLoading = false
          this.warn('ไม่สามารถโหลดรายการจองได้', 'การเชื่อมต่อขัดข้อง โปรดรีเฟรชหน้าจอ')
        }
      )
    },

    // Only from search back to the list, and only once both boxes are empty.
    // The rowsMode guard is also what stops this looping: showList sets the mode
    // to 'list' BEFORE it blanks the two boxes, so the writes it makes re-enter
    // here and leave immediately.
    listWhenSearchEmptied () {
      if (this.rowsMode !== 'search') return
      if (R.hasSearchTerms(this.search)) return
      this.showList()
    },

    // ค้นหาการจอง hands a customer over with ?q= (its จัดการ button, to act on a
    // booking this login may manage) or ?bookSo= (จองวันส่ง on an SO nobody has
    // booked). Read once, on arrival. bookSo runs lookupSo exactly as if the SO
    // had been typed, so every check a typed SO passes still applies. A fourth,
    // ?attachSo=<bookingId>, is its ใส่ SO -- see openAttachSoFromLink.
    //
    // ?appt=1 is the third: a นัดหมาย prefilled from another system -- see
    // R.apptFromQuery, which decides what may be carried. kioskonlineshop uses
    // it for an order whose SAP SO does not exist yet, so nothing in ORDR could
    // describe the delivery. It fills the form and stops there: the date and
    // the slot are chosen here, against the real capacity, exactly as if the
    // whole thing had been typed. A ตำบล it names is selected only if the
    // zipcode's list offers it, so the service area is checked as for a typed one.
    //
    // Known limit: a shop's out-of-area SO is judged against provincesWithRoutes,
    // which watchTrips may not have filled yet on arrival; pressing ค้นหา again
    // re-runs the same check once it has.
    async applyRouteQuery () {
      const q = this.$route.query || {}
      const appt = R.apptFromQuery(q)
      if (appt) {
        this.mode = 'appointment'
        // The mode watcher clears the job on a switch; let it run first, the
        // same order bookSo takes below.
        await this.$nextTick()
        // Before the form: the zipcode watcher reads it once the list loads.
        this.apptLocation = R.apptLocation(q)
        this.form = appt
        // Assigning zipcode fires its watcher, which loads that zipcode's ตำบล
        // list -- serviceableRows() filtered, so an out-of-area zipcode offers
        // nothing to pick and the booking cannot be made here at all.
        this.apptHandover = true
        this.apptReturn = R.apptReturn(q, config.apptReturnDomains)
        return
      }
      if (q.bookSo) {
        // A link sent before the SO was booked, opened after: show the booking
        // it already has rather than a form that would only refuse it. A login
        // that cannot see that booking gets fetchSo's own SO นี้จองแล้ว warning
        // below. A failed lookup falls through -- fetchSo asks again.
        const so = String(q.bookSo).trim()
        let existing = null
        try { existing = await S.findActiveBookingForSo(so) } catch (e) { existing = null }
        if (existing) {
          this.search.soNo = so
          await this.runSearch()
          // The list sits below the form and the calendar; a link that lands
          // on an empty form looks like it did nothing.
          if (this.rows.length) { await this.$nextTick(); this.scrollToList({ smooth: false }); return }
        }
        this.mode = 'so'
        // The mode watcher clears the job on a switch; let it run first.
        await this.$nextTick()
        this.soNumber = String(q.bookSo)
        this.lookupSo()
        return
      }
      if (q.attachSo) return this.openAttachSoFromLink(String(q.attachSo))
      if (!q.q) return
      const c = O.classifyQuery(q.q)
      if (c.kind === 'tel') this.search.tel = c.digits
      else if (c.kind === 'so') this.search.soNo = c.soNo
      else return
      await this.runSearch()
      await this.$nextTick()
      this.scrollToList({ smooth: false })
    },

    searchEverywhere () {
      const q = this.search.soNo || this.search.tel || ''
      this.$router.push({ name: 'orderSearch', query: { q: String(q).trim() } })
    },

    // An up-country row lives in the search results only; the live list has its own
    // subscription, so only a search needs re-running.
    refreshRows () {
      if (this.rowsMode === 'search') this.runSearch()
    },

    onUpSaved (id, date, sapNote) {
      const text = date
        ? `ขึ้นรอบรถใหญ่แล้ว วันส่ง ${date}`
        : 'เข้าคิวรอจัดรอบรถใหญ่แล้ว วันส่งจริงจะแจ้งเมื่อจัดรอบ'
      // A trip's date that did not reach SAP is said, in the words every page uses.
      this.$root.$message('บันทึกแล้ว', sapNote ? [text, sapNote] : text,
        { type: 'alert', color: sapNote ? 'warning' : 'success', width: 420 })
      this.soNumber = ''
      this.rtNumber = ''
      this.rtData = null
    },

    async runSearch () {
      const tel = this.search.tel || ''
      const soNo = this.search.soNo || ''
      if (!R.hasSearchTerms(this.search)) {
        return this.warn('ยังไม่ได้ระบุคำค้น', 'โปรดกรอกเบอร์โทรหรือเลขที่ SO')
      }
      this.searching = true
      try {
        const owners = this.canSeeAll ? null : this.myIdentities
        let rows = await S.searchBookings({ tel: tel, soNo: soNo, owners: owners })
        // An RT and an SO are both bare numbers, so a number is asked both ways:
        // it may be one booking's SO and another's RT (an exchange elsewhere, or
        // a pickup). Merged by id -- an exchange found by its SO is not listed
        // twice. searchBookings reads the phone first, so only a search without one.
        if (!R.normalizeTel(tel) && /^\d+$/.test(String(soNo).trim())) {
          const byRt = await S.searchBookings({ rtNo: String(soNo).trim(), owners: owners })
          const seen = {}
          rows.forEach(r => { seen[r.id] = true })
          rows = rows.concat(byRt.filter(r => !seen[r.id]))
        }
        this.rows = rows
        this.rowsMode = 'search'
        this.expanded = []
      } catch (e) {
        this.warn('ค้นหาไม่สำเร็จ', 'เกิดข้อผิดพลาด โปรดลองใหม่')
      } finally {
        this.searching = false
      }
    },

    // Back to the opening state, all three questions at once: no phone, no SO,
    // today, one day. Anything less leaves the caption describing a filter the
    // controls no longer show.
    clearSearch () {
      this.search = { tel: '', soNo: '', date: R.todayStr() }
      this.rangeMode = 'day'
      // The month listener is still live; re-subscribing is what repopulates
      // the list now that snapshots are allowed to land again. Called
      // explicitly because assigning the same values the watchers already hold
      // fires nothing.
      this.showList()
    },

    // Support searches deliberately return cancelled bookings -- "it was
    // cancelled on the 5th by Nok" is exactly what a caller is ringing to hear.
    // They are shown greyed and are not actionable; without this a cancelled row
    // looked identical to a live one and still offered ย้ายวัน.
    isLive (item) { return R.isActive(item.status) },
    // parseGeo, not a presence test: a mistyped pin is no pin to the driver's
    // นำทาง, so it must not show green here.
    hasPin (item) { return !!R.parseGeo(item.geo) },

    // The owner may act on their own booking; staff may act on any.
    visitMates (item) { return V.mates(item, this.rows) },
    visitOffers (item) { return V.linkCandidates(item, this.rows) },

    async doLinkVisit (item, other) {
      const ok = await this.$root.$message('รวมเป็นจุดส่งเดียวกัน', [
        `${RJ.refText(item)} และ ${RJ.refText(other)}`,
        'จะส่งด้วยรถคันเดียวกันในครั้งเดียว และใช้ที่ว่างของวันเพียง 1 งาน',
        '',
        'ยืนยันหรือไม่?'
      ], { type: 'confirm', color: 'teal', width: 450 })
      if (!ok) return
      this.busyId = item.id
      try {
        await S.linkVisit(item.id, other.id,
          { user: this.myEmail, owners: this.myIdentities, role: this.$store.state.status })
        this.$root.$message('รวมจุดส่งแล้ว', ['คืนที่ว่าง 1 งานให้กับวันนี้แล้ว'], { type: 'alert', color: 'success' })
      } catch (e) {
        this.warn('รวมจุดส่งไม่ได้', e.reason ? this.reasonText(e.reason) : 'เกิดข้อผิดพลาด โปรดลองใหม่')
      } finally {
        this.busyId = null
      }
    },

    async doUnlinkVisit (item) {
      const ok = await this.$root.$message('แยกจุดส่ง', [
        `${RJ.refText(item)} จะเป็นจุดส่งของตัวเอง และใช้ที่ว่างของวันนี้ 1 งาน`,
        '',
        'ยืนยันหรือไม่?'
      ], { type: 'confirm', color: 'warning', width: 450 })
      if (!ok) return
      this.busyId = item.id
      try {
        const r = await S.unlinkVisit(item.id,
          { user: this.myEmail, owners: this.myIdentities, role: this.$store.state.status })
        this.$root.$message('แยกจุดส่งแล้ว',
          [r.over ? 'วันนี้มีงานเกินความสามารถของรถแล้ว' : 'การจองนี้เป็นจุดส่งของตัวเองแล้ว'],
          { type: 'alert', color: r.over ? 'warning' : 'success' })
      } catch (e) {
        this.warn('แยกจุดส่งไม่ได้', e.reason ? this.reasonText(e.reason) : 'เกิดข้อผิดพลาด โปรดลองใหม่')
      } finally {
        this.busyId = null
      }
    },

    canManage (item) {
      if (!this.isLive(item)) return false
      return this.canSeeAll || P.ownedBy(item.user, this.myIdentities)
    },

    // ใส่ SO asks a wider question than canManage: a supervisor of the owner's
    // group may attach too (X.canAttachSo, enforced again by attachSo).
    canAttach (item) { return X.canAttachSo(item, this.attachCtx) },

    watchAwaitingSo () {
      if (this.unsubAwaiting) return
      this.unsubAwaiting = S.subscribeAwaitingSo(
        rows => { this.awaitingSo = rows },
        () => this.warn('ไม่สามารถโหลดนัดหมายรอ SO ได้', 'การเชื่อมต่อขัดข้อง โปรดรีเฟรชหน้าจอ')
      )
    },
    watchUpOrders () {
      if (this.unsubUpOrders) return
      // Updates live, so an order saved from the dialog appears in the card at once.
      this.unsubUpOrders = US.subscribeOrders(
        rows => { this.upOrders = rows },
        () => this.warn('ไม่สามารถโหลดออเดอร์รถใหญ่ได้', 'การเชื่อมต่อขัดข้อง โปรดรีเฟรชหน้าจอ')
      )
    },

    stopAwaitingSo () {
      if (this.unsubAwaiting) this.unsubAwaiting()
      this.unsubAwaiting = null
      this.awaitingSo = []
    },

    // The userProfile directory, which a supervisor's right to attach is read
    // from, may still be loading when a link lands. Waits for it, briefly.
    profilesLoaded () {
      if (this.$store.state.profiles.length) return Promise.resolve()
      return new Promise(resolve => {
        let stop = null
        const timer = setTimeout(() => { if (stop) stop(); resolve() }, 5000)
        stop = this.$watch(() => this.$store.state.profiles.length, n => {
          if (!n) return
          clearTimeout(timer)
          stop()
          resolve()
        })
      })
    },

    // ?attachSo=<bookingId>, from ค้นหาการจอง's ใส่ SO. The link only saves a
    // click: the booking is re-read and the rule asked again here, and attachSo
    // asks it a third time on save.
    async openAttachSoFromLink (bookingId) {
      let b = null
      try { b = await S.getBooking(bookingId) } catch (e) { b = null }
      if (!b) return this.warn('ไม่พบการจอง', 'ไม่พบการจองนี้ หรือโหลดข้อมูลไม่ได้ โปรดลองใหม่')
      if (!R.isActive(b.status)) return this.warn('ใส่เลขที่ SO ไม่ได้', this.reasonText('ALREADY_CANCELLED'))
      if (b.soNo) return this.warn('ใส่เลขที่ SO ไม่ได้', this.reasonText('HAS_SO'))
      await this.profilesLoaded()
      if (!this.canAttach(b)) return this.warn('ใส่เลขที่ SO ไม่ได้', this.reasonText('NOT_ALLOWED'))
      this.openAttachSo(b)
    },

    // A pending proposal must be decided on the panel above before this row's
    // own ย้ายวัน / ยกเลิก can be used again -- otherwise a shop could act
    // straight past a request still awaiting a decision.
    hasPending (item) { return X.isPending(item) },
    scrollToPanel () { window.scrollTo({ top: 0, behavior: 'smooth' }) },

    // The calendar and list are live snapshots; only a search result is not.
    async afterProposal () {
      if (this.rowsMode === 'search') await this.runSearch()
    },

    openMove (item) {
      this.dialog = {
        open: true,
        mode: 'move',
        item: item,
        date: item.date,
        slot: item.slot,
        reason: '',
        note: '',
        saving: false
      }
    },

    // Corrections a customer phones in afterwards: a landmark they forgot, a pin
    // they finally dropped, a product line. Nothing here touches capacity, so
    // there is no date, slot or reason -- see editBooking.
    async openEdit (item) {
      this.dialog = {
        open: true,
        mode: 'edit',
        item: item,
        date: item.date,
        slot: item.slot,
        reason: '',
        note: '',
        fields: {
          geo: item.geo || '',
          landmark: item.landmark || '',
          productList: item.productList || '',
          itemCount: item.itemCount != null ? item.itemCount : '',
          installMinutes: item.installMinutes != null ? item.installMinutes : ''
        },
        dispatchWarn: '',
        checkingDispatch: false,
        sapPin: null,
        saving: false
      }
      // A นัดหมาย has no SO, so there is nothing จัดรถ could have dispatched --
      // and nothing in SAP to compare the pin against either. A pickup has its
      // RT, which จัดรถ dispatches and which holds its pin (RJ.pinNumbers).
      if (item.soNo) await this.checkDispatched(item.soNo)
      else if (RJ.isReturnOnly(item) && item.rtNo) await this.checkDispatched(item.rtNo, { rt: true })
    },

    // What SAP holds that this booking does not, for the offer in the dialog.
    applySapPin () {
      const update = R.sapPinUpdate(this.dialog.fields, this.dialog.sapPin)
      if (!update) return
      if (update.geo !== undefined) this.$set(this.dialog.fields, 'geo', update.geo)
      if (update.landmark !== undefined) this.$set(this.dialog.fields, 'landmark', update.landmark)
    },

    // This page never writes deliveryList, so a correction made here does NOT
    // reach a dispatch sheet the office has already built. Warn only when that
    // is actually the case -- a warning shown every time is a warning nobody
    // reads.
    // `opts.rt`: the number is a pickup's RT, read through GetRT -- ORDN carries
    // the same transNo and pin fields as ORDR.
    async checkDispatched (docNo, opts) {
      const rt = !!(opts && opts.rt)
      this.dialog.checkingDispatch = true
      try {
        // Bounded on purpose: this is advisory, and the ASMX service has been
        // seen to take the better part of a minute. Without a ceiling the
        // dialog sits on "กำลังตรวจสอบ..." while someone waits to type a landmark.
        const res = rt
          ? await axios.post(`${this.webPath}deliveryWebService.asmx/GetRT`, { rtNo: Number(docNo) }, { timeout: 10000 })
          : await axios.post(`${this.webPath}deliveryWebService.asmx/GetSo`, { so: docNo }, { timeout: 10000 })
        const rows = JSON.parse(res.data.d)
        const so = Array.isArray(rows) && rows.length ? rows[0] : null
        // The same response answers a second question. A shop correcting a pin
        // in the order system writes it straight to ORDR, so SAP can hold a
        // newer พิกัด / จุดสังเกต than a booking made days earlier -- offered
        // here rather than applied, because only a person knows whether the
        // booking's own value was the corrected one.
        this.dialog.sapPin = so ? R.soPin(so) : null
        // An RT inherits its sale's sheet; only RJ.rtDispatched says it is dispatched.
        if (so && (rt ? RJ.rtDispatched(so) : so.transNo)) {
          this.dialog.dispatchWarn = `งานนี้ถูกจัดรถแล้ว (ใบจัดรถเลขที่ ${so.transNo}) ` +
            'การแก้ไขที่นี่จะไม่ถูกส่งไปยังใบจัดรถ โปรดแก้ไขในหน้าจัดรถด้วย'
        }
      } catch (e) {
        // A failed check must never block a correction, and must not imply the
        // job is clear when we simply could not find out.
        this.dialog.dispatchWarn = 'ไม่สามารถตรวจสอบสถานะการจัดรถได้ ' +
          'หากงานนี้ถูกจัดรถแล้ว โปรดแก้ไขในหน้าจัดรถด้วย'
      } finally {
        this.dialog.checkingDispatch = false
      }
    },

    // Filling in the SO number a นัดหมาย was booked without. The delivery date
    // is already promised, so nothing here touches it: this dialog only replaces
    // what the appointment held with what SAP says, and says which fields that
    // is before anyone presses save.
    openAttachSo (item) {
      this.dialog = {
        open: true,
        mode: 'so',
        item: item,
        date: item.date,
        slot: item.slot,
        reason: '',
        note: '',
        soNumber: '',
        soFound: null,
        soChanges: [],
        lookingUp: false,
        saving: false
      }
    },

    // Look the number up and show what it would change, rather than writing it
    // and reporting afterwards. The same pipeline the booking form runs, with
    // this booking excluded from the duplicate check and an out-of-area address
    // put to staff instead of refused -- see fetchSo.
    async lookupSoForBooking () {
      const num = String(this.dialog.soNumber || '').trim()
      this.dialog.soFound = null
      this.dialog.soChanges = []
      if (!num) return this.warn('ยังไม่ได้ใส่เลขที่ SO', this.reasonText('NO_SO'))
      this.dialog.lookingUp = true
      try {
        const found = await this.fetchSo(num, { excludeBookingId: this.dialog.item.id, areaOut: 'confirm' })
        if (!found) return
        const incoming = this.soFieldsFrom(found)
        // found.docDate, NOT found.orderDate: the latter falls back to today, and
        // stamping today onto a booking made weeks ago would move its lead-time
        // window forward -- an owner would then be refused a move onto dates
        // that were fine an hour earlier. Absent means leave the booking's own
        // orderDate alone.
        // attachSo re-derives this from the SO's location wherever it can, and
        // falls back to this only when the address named no single ตำบล -- the
        // same order createBooking uses. Carried because the confirmation staff
        // just gave, inside fetchSo, is the only thing that knows about it.
        this.dialog.soFound = {
          soNo: num,
          incoming: incoming,
          // What SAP's own fields held, so a pin the booking keeps where SAP
          // was blank can be written back after the attach.
          sapPin: R.soPin(found.so),
          orderDate: found.docDate,
          outOfArea: found.area && found.area.status === 'out'
        }
        // Exactly the diff attachSo will write to the history, from exactly the
        // same two functions -- so what staff confirm and what the record says
        // afterwards cannot describe different changes.
        this.dialog.soChanges = R.diffFields(this.dialog.item,
          R.mergeSoFields(this.dialog.item, incoming), R.SO_DIFF_FIELDS)
      } catch (e) {
        this.warn('เกิดข้อผิดพลาด', 'ไม่สามารถดึงข้อมูล SO หรือตรวจสอบการจองที่มีอยู่ได้ โปรดลองใหม่')
      } finally {
        this.dialog.lookingUp = false
      }
    },

    fieldLabel (field) { return R.EDIT_FIELD_LABEL_TH[field] || field },

    // '(ว่าง)' rather than an empty gap: an arrow with nothing before it reads
    // as a rendering fault, where '(ว่าง)' reads as the fact it is.
    changeValue (v) {
      const t = String(v == null ? '' : v)
      if (!t) return '(ว่าง)'
      return t.length > 60 ? t.slice(0, 60) + '…' : t
    },

    openCancel (item) {
      this.dialog = {
        open: true,
        mode: 'cancel',
        item: item,
        date: item.date,
        slot: item.slot,
        reason: '',
        note: '',
        saving: false
      }
    },

    async saveDialog () {
      const d = this.dialog
      // A correction needs no reason: the history records every field that
      // changed and what it was, which says more than a dropdown would.
      // Nor does an attach: the history records the number and every field the
      // SO replaced, which is the whole of what happened.
      if (d.mode !== 'edit' && d.mode !== 'so' && !d.reason) {
        return this.warn('ยังไม่ได้เลือกเหตุผล', this.reasonText('NO_REASON'))
      }
      // Saving a number nobody looked up would write whatever was typed and
      // change nothing else -- the opposite of what this dialog promises.
      if (d.mode === 'so' && !d.soFound) {
        return this.warn('ยังไม่ได้ค้นหาเลขที่ SO', 'โปรดกดค้นหาเพื่อตรวจสอบเลขที่ SO ก่อนบันทึก')
      }
      if (d.mode === 'move' && !d.date) {
        return this.warn('ยังไม่ได้เลือกวันที่', 'โปรดเลือกวันที่ใหม่')
      }
      if (d.mode === 'edit' && d.fields.geo && !R.parseGeo(d.fields.geo)) {
        return this.warn('พิกัดไม่ถูกต้อง', this.reasonText('BAD_GEO'))
      }
      // Same bound as submitBooking, and refused here for the same reason: what
      // installTime would clamp on the way out is not what the dialog showed.
      if (d.mode === 'edit' && d.fields.installMinutes) {
        const m = Math.floor(Number(d.fields.installMinutes))
        if (!isFinite(m) || m < I.MIN_INSTALL_MINUTES || m > I.MAX_INSTALL_MINUTES) {
          return this.warn('เวลาติดตั้งไม่ถูกต้อง',
            `เวลาติดตั้งต้องอยู่ระหว่าง ${I.MIN_INSTALL_MINUTES} ถึง ${I.MAX_INSTALL_MINUTES} นาที`)
        }
      }
      // A staff move onto a day that has already passed. bookingService lets it
      // through on purpose -- it is how a delivery that really happened gets its
      // date corrected, and the alert in this dialog promises as much -- but
      // nothing was asking, so a mistyped month committed exactly as quietly as
      // a deliberate correction. The rule does not move: shops are still refused
      // outright by PAST, and staff still get their way. They just have to mean
      // it. Said in days rather than only a date, because "35 วัน" is what makes
      // a typo obvious where "2026-08-01" on its own does not.
      if (d.mode === 'move' && this.canSeeAll && d.date < R.todayStr()) {
        const confirmed = await this.$root.$message(
          'ย้ายไปวันที่ผ่านมาแล้ว',
          [
            `วันที่ใหม่ : ${d.date} (ผ่านมาแล้ว ${this.daysAgo(d.date)} วัน)`,
            'การจองจะไม่แสดงในรายการงานประจำวัน และจะใช้ที่ว่างของวันที่ผ่านมาแล้ว',
            '',
            'ยืนยันการย้ายหรือไม่?'
          ],
          { type: 'confirm', color: 'warning', width: 450 }
        )
        if (!confirmed) return
      }
      d.saving = true
      this.busyId = d.item.id
      try {
        if (d.mode === 'move') {
          const r = await S.moveBooking(d.item.id, {
            date: d.date,
            slot: d.slot,
            reason: d.reason,
            note: d.note || '',
            user: this.myEmail,
            owners: this.myIdentities,
            role: this.$store.state.status
          })
          // A privileged move is allowed to overfill a day; saying so is the
          // only thing standing between that and a driver finding out.
          const over = r.over ? ` — วันนี้มี ${r.total} งาน เกินความสามารถของรถ (${r.cap})` : ''
          // And a staff move may leave an out-of-area booking on a day with no
          // route to its province. That is allowed -- staff keep the last word
          // everywhere in this flow -- but it is not something to find out from
          // the board a week later.
          const stranded = r.stranded
            ? ' — วันที่ปลายทางยังไม่มีเส้นทางไปพื้นที่นี้ การจองนี้จะรอจัดเส้นทาง'
            : ''
          // The customer was promised a new day; SAP has to stop showing the old one.
          const dateFailed = await pushBookDateToSap(this.webPath,
            { soNo: d.item.soNo, rtNo: d.item.rtNo || '', date: d.date, status: d.item.status })
          this.$root.$message('ย้ายวันแล้ว',
            [`ย้ายเป็นวันที่ ${d.date} ช่วง${R.SLOT_LABEL_TH[d.slot]} เรียบร้อยแล้ว${over}${stranded}`,
              bookDateFailedNote(dateFailed)].filter(Boolean),
            { type: 'alert', color: (r.over || r.stranded || dateFailed.length) ? 'warning' : 'success', width: 500 })
        } else if (d.mode === 'so') {
          const changes = await S.attachSo(d.item.id, {
            soNo: d.soFound.soNo,
            so: d.soFound.incoming,
            orderDate: d.soFound.orderDate,
            outOfArea: d.soFound.outOfArea,
            user: this.myEmail,
            owners: this.myIdentities,
            role: this.$store.state.status,
            profiles: this.$store.state.profiles
          })
          const what = changes.length
            ? ` และปรับ${changes.map(c => this.fieldLabel(c.field)).join(', ')} ตามข้อมูลใน SO`
            : ''
          // The booking kept its own พิกัด / จุดสังเกต wherever SAP's was blank
          // (mergeSoFields), so those are what SAP is missing.
          const kept = R.mergeSoFields(d.item, d.soFound.incoming)
          // The booking had no ORDR row to write its date to until now. This is
          // where a นัดหมาย finally reaches SAP, with the date it has held all
          // along -- attachSo does not move it. Both writes go at once: they
          // touch different fields and the user is already waiting.
          const [failed, dateFailed] = await Promise.all([
            pushPinToSap(this.webPath, [d.soFound.soNo], R.sapPinPatch(d.soFound.sapPin, kept)),
            pushBookDateToSap(this.webPath,
              { soNo: d.soFound.soNo, rtNo: d.item.rtNo || '', date: d.item.date, status: d.item.status })
          ])
          const sapNotes = [sapFailedNote(failed), bookDateFailedNote(dateFailed)].filter(Boolean)
          this.$root.$message('ใส่เลขที่ SO แล้ว',
            [`การจองนี้ใช้เลขที่ SO ${d.soFound.soNo} แล้ว${what}`].concat(sapNotes),
            { type: 'alert', color: sapNotes.length ? 'warning' : 'success', width: 500 })
        } else if (d.mode === 'edit') {
          const changes = await S.editBooking(d.item.id, {
            fields: d.fields,
            user: this.myEmail,
            owners: this.myIdentities,
            role: this.$store.state.status
          })
          const what = changes.map(c => R.EDIT_FIELD_LABEL_TH[c.field]).join(', ')
          // Only the fields this edit changed -- to the SO, or a pickup's RT.
          const failed = await pushBookingPinToSap(this.webPath, d.item, R.sapPinPatch(d.item, d.fields))
          this.$root.$message('บันทึกแล้ว',
            [`แก้ไข${what} เรียบร้อยแล้ว`, sapFailedNote(failed)].filter(Boolean),
            { type: 'alert', color: failed.length ? 'warning' : 'success', width: failed.length ? 450 : 400 })
        } else {
          await S.cancelBooking(d.item.id, this.myEmail, d.reason)
          // Clearing matters more than setting: a date left in SAP for a booking
          // that no longer exists is what makes another system promise a
          // delivery nobody is making.
          const dateFailed = await pushBookDateToSap(this.webPath,
            { soNo: d.item.soNo, rtNo: d.item.rtNo || '', date: d.item.date, status: 'cancelled' })
          this.$root.$message('ยกเลิกแล้ว',
            ['ยกเลิกการจองเรียบร้อยแล้ว', bookDateFailedNote(dateFailed, { cleared: true })].filter(Boolean),
            { type: 'alert', color: dateFailed.length ? 'warning' : 'success', width: dateFailed.length ? 450 : 400 })
        }
        this.dialog.open = false
        // A search result is a snapshot, not a live query, so it has to be
        // re-run to show what just changed.
        if (this.rowsMode === 'search') await this.runSearch()
      } catch (e) {
        const msg = e.reason ? this.reasonText(e.reason) : 'เกิดข้อผิดพลาด โปรดลองใหม่'
        const title = { move: 'ย้ายวันไม่สำเร็จ', cancel: 'ยกเลิกไม่สำเร็จ', edit: 'แก้ไขไม่สำเร็จ', so: 'ใส่เลขที่ SO ไม่สำเร็จ' }[d.mode]
        this.warn(title, msg)
      } finally {
        d.saving = false
        this.busyId = ''
      }
    },

    // Who booked it, every move / cancel / correction, and every request with
    // who asked and who decided -- shared with ค้นหาการจอง (bookingHistory.js).
    historyLines (item) {
      return H.timeline(item, { nameOf: this.$personName, today: R.todayStr() }).map(H.lineOf)
    },

    validCars (n) {
      return Number.isInteger(n) && n >= 0
    },

    // Cleared after a successful save, never before one. หมายเหตุ is written to
    // every day in the range and shown under each of them in the calendar, so a
    // note left over from an earlier incident would brand a fortnight of
    // deliveries with the wrong reason the moment someone edits only the dates.
    // จำนวนรถ carries the same risk pointing the other way -- a stale 0 silently
    // closes days nobody meant to close. Resetting to the standing fleet size
    // also makes "back to normal" the default the form opens on.
    resetCapForm () {
      this.capForm = { from: R.todayStr(), to: R.todayStr(), cars: this.configCars, note: '' }
    },

    async saveCapacity () {
      if (!this.validCars(this.capForm.cars)) {
        return this.warn('จำนวนรถไม่ถูกต้อง', 'โปรดกรอกจำนวนรถเป็นจำนวนเต็มตั้งแต่ 0 ขึ้นไป')
      }
      if (!this.capForm.from || !this.capForm.to || this.capForm.from > this.capForm.to) {
        return this.warn('ช่วงวันที่ไม่ถูกต้อง', 'โปรดเลือกวันที่เริ่มต้นและสิ้นสุดให้ถูกต้อง')
      }
      this.savingCap = true
      try {
        const r = await S.setCapacityRange({
          from: this.capForm.from, to: this.capForm.to, cars: this.capForm.cars,
          note: this.capForm.note, user: this.$store.state.user || ''
        })
        // Reducing capacity below existing bookings is allowed on purpose;
        // report it plainly so staff can move or cancel those customers.
        const over = r.overCapacity.length
          ? ` — ${r.overCapacity.length} วันเกินจำนวนที่จองไว้แล้ว (${r.overCapacity.map(o => o.date).join(', ')})`
          : ''
        this.$root.$message('บันทึกแล้ว', `บันทึกแล้ว ${r.written} วัน${over}`,
          { type: 'alert', color: over ? 'warning' : 'success', width: 500 })
        this.resetCapForm()
      } catch (e) {
        // Deliberately NOT reset on failure: the values are what the retry needs.
        this.warn('บันทึกไม่สำเร็จ', 'เกิดข้อผิดพลาด โปรดลองใหม่')
      } finally {
        this.savingCap = false
      }
    },

    async saveStandingConfig () {
      if (!this.validCars(this.defaultCars)) {
        return this.warn('จำนวนรถไม่ถูกต้อง', 'โปรดกรอกจำนวนรถเป็นจำนวนเต็มตั้งแต่ 0 ขึ้นไป')
      }
      if (!['am', 'pm'].every(k => this.validCars(this.defaultPerCar[k]))) {
        return this.warn('จำนวนงานต่อคันไม่ถูกต้อง', 'โปรดกรอกจำนวนงานต่อคันเป็นจำนวนเต็มตั้งแต่ 0 ขึ้นไป')
      }
      // An independent sanity bound. It no longer names the horizon, which has
      // become a setting of its own -- the two are checked against each other
      // below, which is where the constraint that actually matters lives.
      if (!this.validCars(this.defaultLeadDays) || this.defaultLeadDays > 30) {
        return this.warn('จำนวนวันจองล่วงหน้าไม่ถูกต้อง',
          'โปรดกรอกเป็นจำนวนเต็มตั้งแต่ 0 ถึง 30 วันทำการ')
      }
      if (!this.validCars(this.defaultHorizonDays) ||
          this.defaultHorizonDays < 1 || this.defaultHorizonDays > R.MAX_HORIZON_DAYS) {
        return this.warn('จำนวนวันที่เปิดจองล่วงหน้าไม่ถูกต้อง',
          `โปรดกรอกเป็นจำนวนเต็มตั้งแต่ 1 ถึง ${R.MAX_HORIZON_DAYS} วัน`)
      }
      // The pair, not each on its own. A lead landing past the horizon leaves a
      // calendar with no selectable day in it and nothing on screen to say why,
      // so it is refused here rather than saved and puzzled over afterwards.
      if (!R.hasBookableDay(R.todayStr(), this.defaultLeadDays, this.defaultHorizonDays)) {
        return this.warn('ตั้งค่านี้จะไม่เหลือวันให้จอง',
          `จองล่วงหน้า ${this.defaultLeadDays} วันทำการ เลยวันสุดท้ายที่เปิดจอง (${this.defaultHorizonDays} วัน) โปรดเพิ่มจำนวนวันที่เปิดจอง`)
      }
      this.savingCap = true
      try {
        const perCar = R.withDerivedTotal(this.defaultPerCar)
        await S.setStandingConfig({
          cars: this.defaultCars,
          perCar: perCar,
          leadDays: this.defaultLeadDays,
          horizonDays: this.defaultHorizonDays,
          user: this.$store.state.user || ''
        })
        this.configCars = this.defaultCars
        this.configPerCar = Object.assign({}, perCar)
        this.configLeadDays = R.normalizeLeadDays(this.defaultLeadDays)
        this.configHorizonDays = R.normalizeHorizonDays(this.defaultHorizonDays)
        // The horizon is the far end of the trips listener's window, so
        // widening it here without re-subscribing would leave the board
        // missing routes the listener stopped covering.
        this.watchTrips()
        // Written back so a cleared field shows the value that was actually
        // saved rather than the blank that produced it.
        this.defaultPerCar = Object.assign({}, perCar)
        const caps = R.capacityForCars(this.defaultCars, perCar)
        this.$root.$message('บันทึกแล้ว',
          `จำนวนรถมาตรฐาน ${this.defaultCars} คัน · เช้า ${caps.am} · บ่าย ${caps.pm} · รวม ${caps.total} ต่อวัน` +
          ` · จองล่วงหน้า ${this.configLeadDays} วันทำการ · จองได้ถึง ${this.latest}`,
          { type: 'alert', color: 'success', width: 500 })
      } catch (e) {
        this.warn('บันทึกไม่สำเร็จ', 'เกิดข้อผิดพลาด โปรดลองใหม่')
      } finally {
        this.savingCap = false
      }
    },

    async runRecount () {
      this.savingCap = true
      try {
        const r = await S.recountRange(this.capForm.from, this.capForm.to)
        // Bookings written before this page had a search box carry no telDigits
        // and no orderDate. The repair button is the natural place to fill them:
        // it already scans the same range, and both are derived, never invented.
        const b = await S.backfillSearchFields(this.capForm.from, this.capForm.to)
        this.$root.$message('คำนวณใหม่เสร็จแล้ว',
          `ตรวจ ${r.days} วัน · แก้ไข ${r.changed.length} วัน · เติมข้อมูลค้นหา ${b.patched}/${b.scanned} รายการ`,
          { type: 'alert', color: 'success', width: 400 })
      } catch (e) {
        this.warn('คำนวณใหม่ไม่สำเร็จ', 'เกิดข้อผิดพลาด โปรดลองใหม่')
      } finally {
        this.savingCap = false
      }
    }
  },
  mounted () { this.init() },
  beforeDestroy () {
    this.stopApptCountdown()
    if (this.unsubMonth) this.unsubMonth()
    if (this.unsubList) this.unsubList()
    if (this.unsubTrips) this.unsubTrips()
    this.stopAwaitingSo()
    if (this.unsubUpOrders) this.unsubUpOrders()
  }
}
</script>

<style scoped>
/* Vuetify sets .v-messages__message to line-height:12px at font-size:12px, so
   the line box has no room above the glyph. Thai upper vowel and tone marks
   render there and get clipped -- "เปิดแผนที่" shows as "เปิดแผนที". Every hint
   and validation message on this page is Thai, so give them room to breathe. */
::v-deep .v-messages__message {
  line-height: 1.6;
}

/* งานต่อคัน (รวม) is computed from เช้า + บ่าย. It has to keep the exact geometry
   of the three fields beside it -- `filled` boxed it and pushed it out of line --
   so the only cue that it cannot be typed into is the muted value. */
.section-toggle {
  cursor: pointer;
}

.derived-field ::v-deep input {
  color: rgba(0, 0, 0, 0.6);
}
</style>
